/**
 * Modul 1111 — Zlato: feed i analitika. Fullscreen redizajn Modula 1 (live feed).
 *
 * Tri tipa transakcija u jednom toku (prodaja inv. zlata, otkup inv. zlata,
 * otkup loma), kartice vizualno identicne a razlikuju se bojom tipa. Povijest
 * ulazi seamless kroz unified tablice (m1b), a detalj retka nosi cjenovni
 * lanac: ponuda -> spot izdavanje -> spot prihvat -> hedge -> fixing ->
 * knjigovodstvo, s provenijencijom po koraku.
 *
 * Dizajn NAMJERNO odstupa od ostatka BIS-a (zahtjev korisnika): svijetla
 * analitika (Linear/Stripe ton), boje tipova kao jedini akcenti.
 *
 * Backend: /api/ado-1111/* (App\Support\AdoModule1111\Pipeline).
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { api, mutate, downloadFile } from '../shared/api';
import { AnimatedNumber, GeoScatterMap, GroupedBars, HeatmapGrid, LineChart, Spark, type GeoPoint, type Series } from './ado1111Charts';

type Locale = 'hr' | 'en';
export type TxnType = 'prodaja' | 'otkup_inv' | 'otkup_lom';

// ------------------------------------------------------------------ API tipovi

/** Response cache info s backenda (cachedJson): kad je izracunato i je li iz cachea. */
type CacheInfo = { cached_at: string; ttl: number; hit: boolean };

type KpiCell = {
    broj: number; eur: number; fine_g: number;
    eur_per_g: number | null; spot_eur_g: number | null; spread_pct: number | null;
    ruc_eur?: number; otvorene?: number; realizirane?: number; realizirano_eur?: number;
    // lijevak (prodaja): ponude izdane u razdoblju + koliko ih je prihvaceno
    ponude?: number; ponude_eur?: number; prihvacene?: number; prihvaceno_eur?: number; konverzija_pct?: number | null;
};
type SummaryResp = { range: { from: string; to: string }; prodaja: KpiCell; otkup_inv: KpiCell; otkup_lom: KpiCell; _cache?: CacheInfo };

type FeedRow = {
    type: TxnType; id: number; ref: string; ts: string; accepted_ts: string | null;
    branch: string; country: string; subregion: string; group: string;
    artikl: string | null; metal: string | null; kolicina: number | null; fine_g: number | null;
    eur: number; eur_per_g: number | null; spot_eur_g: number | null; spread_pct: number | null;
    status: string; source: string; partner: string | null; ruc_eur?: number | null;
};
type FeedResp = { rows: FeedRow[]; total: number; totals_by_type: Record<TxnType, number>; page: number; per_page: number };

type MetaBranch = { branch: string; country: string; subregion: string; group: string };
type MetaResp = {
    types: TxnType[]; dimensions: string[]; branches: MetaBranch[];
    geo_tree: Record<string, Record<string, Record<string, string[]>>>;
    ranges: Record<TxnType, { min: string | null; max: string | null }>;
    coverage_prodaja: { source: string; broj: number; od: string | null; do: string | null }[];
    plan_years: number[]; metals: string[]; statuses: string[];
    sale_basis?: string[];
    channels?: { veleprodaja: boolean; mjenjacnica_zalog: boolean; lom_finoca: boolean };
};

type ACell = { broj: number; eur: number; fine_g: number; eur_per_g: number | null; spot_eur_g: number | null; spread_pct: number | null; ruc_eur: number };
type ARow = { key: string; b_key: string | null; a: Record<string, ACell> | null; b: Record<string, ACell> | null };
type AnalyticsResp = {
    dimension: string; compare: string | null;
    a_range: { from: string; to: string }; b_range: { from: string; to: string } | null;
    note: string | null; rows: ARow[]; _cache?: CacheInfo;
};
type HeatCell = { dow: number; hour: number; type: TxnType; broj: number; eur: number };
type BranchCmpRow = { branch: string; country: string; subregion: string; group: string; metrics: Record<string, ACell>; trend: { month: string; eur: number }[] };
type MapPointRow = { branch: string; country: string; subregion: string; group: string; lat: number | null; lng: number | null; metrics: Record<string, ACell> };
type MapResp = { points: MapPointRow[]; unplaced: MapPointRow[]; _cache?: CacheInfo };

type HedgeLink = { doc: string; trade_date: string; deal_type: string; pair: string; price: number | null; allocated_g: number; allocated_eur: number | null };
type HedgeCtx = { deal_type: string; broj: number; fine_g: number; eur: number };
type BookInfo = { racuni?: string[]; nabavna_eur?: number | null; neto_eur?: number | null; bruto_eur?: number | null; ruc_eur?: number | null; prihod_eur?: number | null; napomena?: string };
type ChainStep = {
    key: string; label: string; available: boolean;
    ts?: string | null; eur?: number | null; eur_per_g?: number | null; source?: string | null;
    spread_pct?: number | null; note?: string | null; links?: HedgeLink[]; context?: HedgeCtx[] | null; book?: BookInfo | null;
};
type DetailResp = { row: FeedRow; chain: ChainStep[]; field_sources: Record<string, unknown> | null };

type PlanApiRow = { id: number; year: number; month: number; branch: string | null; txn_type: TxnType; promet_eur: number | null; fine_g: number | null; ruc_eur: number | null; broj: number | null; note: string | null };
type PvaRow = { month: number; actual: { eur: number; fine_g: number; ruc_eur: number; broj: number } | null; plan: { eur: number; fine_g: number; ruc_eur: number; broj: number } | null };
type PvaResp = { year: number; txn_type: TxnType; branch: string | null; rows: PvaRow[] };

// prodajni lijevak: ponuda -> prihvat (predracun/racun), po mjesecu izdavanja ponude
type FunnelRow = {
    mjesec: string; ponude: number; ponude_eur: number; prihvacene: number; prihvaceno_eur: number;
    konverzija_pct: number | null; predracuni: number; racuni: number; istekle: number; avg_sati_do_prihvata: number | null;
    puni_lijevak?: boolean;
};
type FunnelTotals = { ponude: number; ponude_eur: number; prihvacene: number; prihvaceno_eur: number; predracuni: number; racuni: number; istekle: number; konverzija_pct: number | null };
type FunnelResp = { rows: FunnelRow[]; totals: FunnelTotals; napomena?: string; _cache?: CacheInfo };

type FinocaRow = { metal: string; finoca: number | null; broj: number; eur: number; izvor: string; udio_pct: number | null; au_fine_g?: number; ag_fine_g?: number };
type FinocaResp = { rows: FinocaRow[]; total_eur: number; ira_available: boolean; napomena?: string; _cache?: CacheInfo };

type VeleMonthlyRow = { mjesec: string; company: string; branch: string; broj: number; eur: number; fine_g: number };
type VeleRow = { ts: string; ref: string; company: string; branch: string; metal: string | null; eur: number; fine_g: number | null; kolicina: number | null };
type VeleResp = { available: boolean; rows: VeleRow[]; monthly: VeleMonthlyRow[]; totals: { broj: number; eur: number; fine_g: number } | null; napomena?: string; _cache?: CacheInfo };

type KanalWeeklyRow = { mjesec: string; poslovnica: string; broj: number; eur: number };
type KanalTabakRow = { mjesec: string; poslovnica: string; metric: string; eur: number };
type KanalBlock = { weekly: KanalWeeklyRow[]; tabak: KanalTabakRow[] };
type KanaliResp = { aggregate: boolean; mjenjacnica: KanalBlock; zalog: KanalBlock; sources: string[]; napomena?: string; _cache?: CacheInfo };

// pregled: 4 vremenska prozora vs LY (tx/EUR/RUC), segmenti, trend cijene
type PregledPeriod = {
    label: string; range: { from: string; to: string }; range_ly: { from: string; to: string };
    tx: number; tx_ly: number; tx_diff: number; tx_pct: number | null;
    eur: number; eur_ly: number; eur_diff: number; eur_pct: number | null;
    ruc: number | null; ruc_ly: number | null; ruc_diff: number | null; ruc_pct: number | null;
    ruc_n: number; ruc_n_ly: number;
};
type PregledSegRow = {
    key?: string; label: string;
    tx: number; tx_ly: number; tx_pct: number | null;
    eur: number; eur_ly: number; eur_pct: number | null;
    ruc: number | null; ruc_ly: number | null;
};
type PregledSegments = { range: { from: string; to: string }; range_ly: { from: string; to: string }; buckets: PregledSegRow[] };
type PregledPriceSeries = { available: boolean; unit: string; series: { date: string; close: number }[]; change_pct: number | null };
type PregledResp = {
    periods: Record<'day' | 'week' | 'month' | 'custom', PregledPeriod>;
    amount_segments: PregledSegments; customer_segments: PregledSegments;
    price_trend: { gold: PregledPriceSeries; silver: PregledPriceSeries };
    ruc_from: string | null; napomena?: string; _cache?: CacheInfo;
};

// ------------------------------------------------------------------- konstante

const C = {
    bg: '#f6f7f9',
    surface: '#ffffff',
    border: '#e5e7eb',
    borderSoft: '#f0f1f3',
    text: '#111827',
    muted: '#6b7280',
    soft: '#9ca3af',
    good: '#059669',
    bad: '#dc2626',
};

export const TYPE_META: Record<TxnType, { color: string; bg: string; border: string; hr: string; en: string; shortHr: string; shortEn: string }> = {
    prodaja: { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', hr: 'Prodaja inv. zlata', en: 'Investment gold sales', shortHr: 'Prodaja', shortEn: 'Sales' },
    otkup_inv: { color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', hr: 'Otkup inv. zlata', en: 'Investment buyback', shortHr: 'Otkup inv.', shortEn: 'Inv. buyback' },
    otkup_lom: { color: '#d97706', bg: '#fffbeb', border: '#fde68a', hr: 'Otkup loma', en: 'Scrap buyback', shortHr: 'Lom', shortEn: 'Scrap' },
};
const ALL_TYPES: TxnType[] = ['prodaja', 'otkup_inv', 'otkup_lom'];
const UKUPNO_COLOR = '#111827';

const TIME_DIMS = ['dan', 'tjedan', 'mjesec', 'godina'];
const DIM_LABELS: Record<string, [string, string]> = {
    dan: ['Dan', 'Day'], tjedan: ['Tjedan', 'Week'], mjesec: ['Mjesec', 'Month'], godina: ['Godina', 'Year'],
    poslovnica: ['Poslovnica', 'Branch'], drzava: ['Država', 'Country'], subregija: ['Pod-regija', 'Subregion'], grupa: ['Grupa država', 'Country group'],
    sat: ['Sat u danu', 'Hour of day'], dan_u_tjednu: ['Dan u tjednu', 'Day of week'], metal: ['Metal', 'Metal'], tip: ['Tip transakcije', 'Transaction type'], status: ['Status', 'Status'],
};
export type MetricKey = 'eur' | 'broj' | 'fine_g' | 'eur_per_g' | 'spread_pct' | 'ruc_eur';
const METRICS: { key: MetricKey; hr: string; en: string }[] = [
    { key: 'eur', hr: 'Promet EUR', en: 'Turnover EUR' },
    { key: 'broj', hr: 'Broj transakcija', en: 'Transaction count' },
    { key: 'fine_g', hr: 'Fino zlato (g)', en: 'Fine gold (g)' },
    { key: 'eur_per_g', hr: 'Pros. EUR/g', en: 'Avg EUR/g' },
    { key: 'spread_pct', hr: 'Spread vs spot %', en: 'Spread vs spot %' },
    { key: 'ruc_eur', hr: 'RUC EUR (lom)', en: 'Margin EUR (scrap)' },
];
const MONTHS_HR = ['Sij', 'Velj', 'Ožu', 'Tra', 'Svi', 'Lip', 'Srp', 'Kol', 'Ruj', 'Lis', 'Stu', 'Pro'];
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOWS_HR = ['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned'];
const DOWS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const SOURCE_LABELS: Record<string, [string, string]> = {
    blagajna_json: ['Live blagajna', 'Live POS'],
    faros_izvoz: ['Faros izvoz', 'Faros export'],
    faros_pdf: ['Faros PDF (povijest)', 'Faros PDF (history)'],
    faros_xls_header: ['Faros XLS (povijest)', 'Faros XLS (history)'],
    live: ['Live blagajna', 'Live POS'],
    ira_xls: ['IRA XLS (povijest)', 'IRA XLS (history)'],
    report_15min: ['Live report', 'Live report'],
    blagajna: ['Live blagajna', 'Live POS'],
    unified_live: ['Live feed', 'Live feed'],
    derived_daily: ['Izveden iz dnevnog spota', 'Derived from daily spot'],
};

// ------------------------------------------------------------------- format

const nf0 = new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nfG = new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 1 });

export function fmtEur(v: number | null | undefined, compact = false): string {
    if (v === null || v === undefined) return '-';
    if (compact && Math.abs(v) >= 1_000_000) return nf2.format(v / 1_000_000) + ' M€';
    if (compact && Math.abs(v) >= 10_000) return nf0.format(Math.round(v / 1000)) + ' k€';
    return nf2.format(v) + ' €';
}
export function fmtN(v: number | null | undefined): string {
    return v === null || v === undefined ? '-' : nf0.format(v);
}
export function fmtG(v: number | null | undefined): string {
    if (v === null || v === undefined) return '-';
    if (Math.abs(v) >= 100_000) return nfG.format(v / 1000) + ' kg';
    return nfG.format(v) + ' g';
}
export function fmtEurG(v: number | null | undefined): string {
    return v === null || v === undefined ? '-' : nf2.format(v) + ' €/g';
}
export function fmtPct(v: number | null | undefined, sign = true): string {
    if (v === null || v === undefined) return '-';
    return (sign && v > 0 ? '+' : '') + nf2.format(v) + ' %';
}
function fmtD(s?: string | null): string {
    if (!s) return '-';
    const p = s.slice(0, 10).split('-');
    return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}.` : s;
}
function fmtDT(s?: string | null): string {
    if (!s) return '-';
    const time = s.length >= 16 ? s.slice(11, 16) : '';
    return fmtD(s) + (time && time !== '00:00' ? ' ' + time : '');
}
function metricVal(c: ACell | KpiCell | null | undefined, m: MetricKey): number | null {
    if (!c) return null;
    const v = (c as Record<string, number | null | undefined>)[m];
    return v === undefined || v === null ? null : v;
}
function fmtMetric(v: number | null, m: MetricKey): string {
    if (m === 'eur' || m === 'ruc_eur') return fmtEur(v, true);
    if (m === 'broj') return fmtN(v);
    if (m === 'fine_g') return fmtG(v);
    if (m === 'eur_per_g') return fmtEurG(v);
    return fmtPct(v);
}

// ------------------------------------------------------------------- filteri

export type Filters = {
    dateFrom: string; dateTo: string; types: TxnType[];
    group: string; country: string; subregion: string; branches: string[];
    metal: string; status: string; q: string; minEur: string;
};
function isoDaysAgo(days: number): string {
    const d = new Date(Date.now() - days * 86_400_000);
    return d.toISOString().slice(0, 10);
}
const TODAY = new Date().toISOString().slice(0, 10);
export function defaultFilters(): Filters {
    return { dateFrom: isoDaysAgo(30), dateTo: TODAY, types: [...ALL_TYPES], group: '', country: '', subregion: '', branches: [], metal: '', status: '', q: '', minEur: '' };
}
export function buildQs(f: Filters, extra?: Record<string, string>): string {
    const p = new URLSearchParams();
    p.set('date_from', f.dateFrom);
    p.set('date_to', f.dateTo);
    if (f.types.length > 0 && f.types.length < ALL_TYPES.length) p.set('types', f.types.join(','));
    if (f.group) p.set('group', f.group);
    if (f.country) p.set('country', f.country);
    if (f.subregion) p.set('subregion', f.subregion);
    if (f.branches.length > 0) p.set('branches', f.branches.join('|'));
    if (f.metal) p.set('metal', f.metal);
    if (f.status) p.set('status', f.status);
    if (f.q.trim() !== '') p.set('q', f.q.trim());
    if (f.minEur.trim() !== '') p.set('min_eur', f.minEur.trim());
    if (extra) Object.entries(extra).forEach(([k, v]) => { if (v !== '') p.set(k, v); });
    return p.toString();
}

// ------------------------------------------------------------------- stilovi

const S: Record<string, CSSProperties> = {
    input: {
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
        padding: '7px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit',
    },
    label: { fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4, display: 'block' },
    card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 },
    btn: {
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
        padding: '7px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
    },
    btnPrimary: {
        background: C.text, border: `1px solid ${C.text}`, borderRadius: 8, color: '#fff',
        padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
    },
    th: { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
    td: { padding: '8px 10px', fontSize: 13, color: C.text, borderBottom: `1px solid ${C.borderSoft}`, whiteSpace: 'nowrap' },
    tdNum: { padding: '8px 10px', fontSize: 13, color: C.text, borderBottom: `1px solid ${C.borderSoft}`, whiteSpace: 'nowrap', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
};

// ------------------------------------------------------------------- primitive

function TypeDot({ type, size = 8 }: { type: TxnType; size?: number }) {
    return <span style={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', background: TYPE_META[type].color, flexShrink: 0 }} />;
}

function TypeChip({ type, active, onClick, locale, count }: { type: TxnType; active: boolean; onClick: () => void; locale: Locale; count?: number }) {
    const m = TYPE_META[type];
    return (
        <button
            onClick={onClick}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999,
                border: `1px solid ${active ? m.color : C.border}`,
                background: active ? m.bg : C.surface,
                color: active ? m.color : C.muted, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}
        >
            <TypeDot type={type} />
            {locale === 'hr' ? m.shortHr : m.shortEn}
            {count !== undefined ? <span style={{ fontWeight: 400, opacity: 0.75 }}>{fmtN(count)}</span> : null}
        </button>
    );
}

function Badge({ children, color, bg }: { children: ReactNode; color?: string; bg?: string }) {
    return (
        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, color: color ?? C.muted, background: bg ?? C.borderSoft, whiteSpace: 'nowrap' }}>
            {children}
        </span>
    );
}

function DeltaBadge({ a, b }: { a: number | null; b: number | null }) {
    if (a === null || b === null || b === 0) return <span style={{ color: C.soft, fontSize: 12 }}>-</span>;
    const d = (100 * (a - b)) / Math.abs(b);
    const up = d >= 0;
    return (
        <span style={{ color: up ? C.good : C.bad, fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {up ? '▲' : '▼'} {nf2.format(Math.abs(d))} %
        </span>
    );
}

/** "Podaci: datum vrijeme · cache" - svjezina odgovora iz response cachea. */
function CacheNote({ info, locale }: { info?: CacheInfo | null; locale: Locale }) {
    if (!info) return null;
    const time = info.cached_at.length >= 16 ? info.cached_at.slice(11, 16) : '';
    return (
        <span
            style={{ fontSize: 11, color: C.soft, whiteSpace: 'nowrap' }}
            title={(locale === 'hr' ? 'Cache TTL: ' : 'Cache TTL: ') + Math.round(info.ttl / 60) + ' min'}
        >
            {locale === 'hr' ? 'Podaci' : 'Data'}: {fmtD(info.cached_at)} {time}{info.hit ? ' · cache' : ''}
        </span>
    );
}

function Spinner({ label }: { label: string }) {
    return (
        <div style={{ padding: 32, textAlign: 'center', color: C.muted, fontSize: 13 }}>
            <span style={{ display: 'inline-block', width: 14, height: 14, border: `2px solid ${C.border}`, borderTopColor: C.muted, borderRadius: '50%', marginRight: 8, verticalAlign: -2, animation: 'ado1111spin 0.8s linear infinite' }} />
            {label}
        </div>
    );
}

// ------------------------------------------------------------------- KPI kartica

/** Identican layout za sva tri tipa; razlika je SAMO boja/tip (zahtjev korisnika). */
function KpiCard({ type, cell, locale, active, onToggle }: { type: TxnType; cell: KpiCell | null; locale: Locale; active: boolean; onToggle: () => void }) {
    const m = TYPE_META[type];
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    return (
        <div
            onClick={onToggle}
            style={{
                ...S.card, flex: 1, minWidth: 260, cursor: 'pointer', position: 'relative', overflow: 'hidden',
                opacity: active ? 1 : 0.45, borderColor: active ? m.border : C.border, transition: 'opacity 120ms',
            }}
        >
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: m.color }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <TypeDot type={type} size={10} />
                <span style={{ fontSize: 13, fontWeight: 700, color: m.color }}>{locale === 'hr' ? m.hr : m.en}</span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: C.muted }}>{cell ? fmtN(cell.broj) + ' txn' : '-'}</span>
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color: C.text, fontVariantNumeric: 'tabular-nums', marginBottom: 2 }}>
                <AnimatedNumber value={cell ? cell.eur : null} format={(v) => fmtEur(v, true)} />
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
                <AnimatedNumber value={cell ? cell.fine_g : null} format={fmtG} /> {t('finog', 'fine')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, fontSize: 12 }}>
                <div>
                    <div style={{ color: C.soft, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>{t('Pros. cijena', 'Avg price')}</div>
                    <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtEurG(cell?.eur_per_g ?? null)}</div>
                </div>
                <div>
                    <div style={{ color: C.soft, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>{t('Pros. spot', 'Avg spot')}</div>
                    <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtEurG(cell?.spot_eur_g ?? null)}</div>
                </div>
                <div>
                    <div style={{ color: C.soft, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>Spread</div>
                    <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: (cell?.spread_pct ?? 0) >= 0 ? C.good : C.bad }}>{fmtPct(cell?.spread_pct ?? null)}</div>
                </div>
            </div>
            <div style={{ marginTop: 10, minHeight: 18, fontSize: 12, color: C.muted, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {type === 'prodaja' && cell ? (
                    <>
                        <span>{t('Ponude', 'Offers')}: <b style={{ color: C.text }}>{fmtN(cell.ponude ?? 0)}</b> ({fmtEur(cell.ponude_eur ?? 0, true)})</span>
                        <span>{t('Prihvaćene', 'Accepted')}: <b style={{ color: C.text }}>{fmtN(cell.prihvacene ?? cell.realizirane ?? 0)}</b></span>
                        <span>{t('Konv.', 'Conv.')}: <b style={{ color: C.text }}>{cell.konverzija_pct !== null && cell.konverzija_pct !== undefined ? fmtPct(cell.konverzija_pct, false) : '-'}</b></span>
                    </>
                ) : null}
                {type === 'otkup_lom' && cell ? (
                    <span>RUC: <b style={{ color: C.text }}>{fmtEur(cell.ruc_eur ?? 0, true)}</b></span>
                ) : null}
                {type === 'otkup_inv' ? (
                    <span style={{ color: C.soft }}>{t('Samo live podaci (od 25.05.2026.)', 'Live data only (from 25 May 2026)')}</span>
                ) : null}
            </div>
        </div>
    );
}

// ------------------------------------------------------------------- feed

function sourceLabel(src: string | null | undefined, locale: Locale): string {
    if (!src) return '-';
    const l = SOURCE_LABELS[src];
    return l ? (locale === 'hr' ? l[0] : l[1]) : src;
}

function FeedRowCard({ row, locale, onOpen }: { row: FeedRow; locale: Locale; onOpen: () => void }) {
    const m = TYPE_META[row.type];
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const statusColor = row.status === 'realizirana' ? C.good : row.status === 'otvorena' ? '#b45309' : C.soft;
    return (
        <div
            onClick={onOpen}
            style={{
                display: 'grid',
                gridTemplateColumns: '4px 118px 1.4fr 1.6fr 110px 130px 150px 120px',
                gap: 12, alignItems: 'center', background: C.surface, border: `1px solid ${C.border}`,
                borderRadius: 10, padding: '10px 14px 10px 0', cursor: 'pointer',
            }}
        >
            <div style={{ width: 4, alignSelf: 'stretch', background: m.color, borderRadius: '10px 0 0 10px' }} />
            <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{fmtDT(row.ts)}</div>
                <div style={{ fontSize: 11, color: m.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                    <TypeDot type={row.type} size={6} />{locale === 'hr' ? m.shortHr : m.shortEn}
                </div>
            </div>
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.branch}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{row.country}{row.subregion !== '-' ? ' · ' + row.subregion : ''}</div>
            </div>
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.artikl ?? '-'}</div>
                <div style={{ fontSize: 11, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.ref}{row.partner ? ' · ' + row.partner : ''}
                </div>
            </div>
            <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{row.fine_g !== null ? fmtG(row.fine_g) : '-'}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{row.metal ?? '-'}{row.kolicina !== null ? ' · ' + nfG.format(row.kolicina) + ' kom' : ''}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtEur(row.eur)}</div>
                <div style={{ fontSize: 11, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{row.eur_per_g !== null ? fmtEurG(row.eur_per_g) : '-'}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: C.text }}>
                    {t('spot', 'spot')} {row.spot_eur_g !== null ? fmtEurG(row.spot_eur_g) : '-'}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: row.spread_pct === null ? C.soft : row.spread_pct >= 0 ? C.good : C.bad }}>
                    {row.spread_pct !== null ? fmtPct(row.spread_pct) : '-'}
                    {row.type === 'otkup_lom' && row.ruc_eur !== null && row.ruc_eur !== undefined ? <span style={{ color: C.muted, fontWeight: 400 }}> · RUC {fmtEur(row.ruc_eur, true)}</span> : null}
                </div>
            </div>
            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <Badge color={statusColor} bg={row.status === 'realizirana' ? '#ecfdf5' : row.status === 'otvorena' ? '#fffbeb' : C.borderSoft}>{row.status}</Badge>
                <span style={{ fontSize: 10, color: C.soft }}>{sourceLabel(row.source, locale)}</span>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------- detail drawer

function ChainStepView({ step, locale }: { step: ChainStep; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const ok = step.available;
    return (
        <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: ok ? C.text : C.borderSoft, color: ok ? '#fff' : C.soft, fontSize: 11, fontWeight: 700,
                }}>{ok ? '✓' : '·'}</div>
                <div style={{ width: 2, flex: 1, background: C.borderSoft, marginTop: 4 }} />
            </div>
            <div style={{ paddingBottom: 18, flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: ok ? C.text : C.soft }}>{step.label}</div>
                {ok ? (
                    <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: C.text }}>
                        {step.eur !== null && step.eur !== undefined ? <span>{fmtEur(step.eur)}</span> : null}
                        {step.eur_per_g !== null && step.eur_per_g !== undefined ? <span style={{ fontWeight: 600 }}>{fmtEurG(step.eur_per_g)}</span> : null}
                        {step.spread_pct !== null && step.spread_pct !== undefined ? (
                            <span style={{ color: step.spread_pct >= 0 ? C.good : C.bad, fontWeight: 600 }}>{fmtPct(step.spread_pct)} vs spot</span>
                        ) : null}
                        {step.ts ? <span style={{ color: C.muted }}>{fmtDT(step.ts)}</span> : null}
                        {step.source ? <Badge>{sourceLabel(step.source, locale)}</Badge> : null}
                    </div>
                ) : null}
                {step.book ? (
                    <div style={{ marginTop: 6, background: C.bg, borderRadius: 8, padding: '8px 10px', fontSize: 12, display: 'grid', gap: 3 }}>
                        {step.book.nabavna_eur !== null && step.book.nabavna_eur !== undefined ? <div>{t('Nabavna', 'Cost')}: <b>{fmtEur(step.book.nabavna_eur)}</b></div> : null}
                        {step.book.neto_eur !== null && step.book.neto_eur !== undefined ? <div>{t('Neto (bez PDV)', 'Net (ex VAT)')}: <b>{fmtEur(step.book.neto_eur)}</b></div> : null}
                        {step.book.prihod_eur !== null && step.book.prihod_eur !== undefined ? <div>{t('Prihod (TCA)', 'Revenue (TCA)')}: <b>{fmtEur(step.book.prihod_eur)}</b></div> : null}
                        {step.book.bruto_eur !== null && step.book.bruto_eur !== undefined ? <div>{t('Bruto (s PDV)', 'Gross (incl. VAT)')}: <b>{fmtEur(step.book.bruto_eur)}</b></div> : null}
                        {step.book.ruc_eur !== null && step.book.ruc_eur !== undefined ? (
                            <div>RUC: <b style={{ color: (step.book.ruc_eur ?? 0) >= 0 ? C.good : C.bad }}>{fmtEur(step.book.ruc_eur)}</b></div>
                        ) : null}
                        {step.book.racuni && step.book.racuni.length > 0 ? <div style={{ color: C.muted }}>{t('Računi', 'Invoices')}: {step.book.racuni.join(', ')}</div> : null}
                    </div>
                ) : null}
                {step.links && step.links.length > 0 ? (
                    <div style={{ marginTop: 6, background: C.bg, borderRadius: 8, padding: '8px 10px', fontSize: 12, display: 'grid', gap: 4 }}>
                        {step.links.map((l, i) => (
                            <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <b>{l.doc}</b><span>{l.deal_type}</span><span>{l.pair}</span>
                                <span>{fmtG(l.allocated_g)}</span>
                                {l.price !== null ? <span>@ {nf2.format(l.price)}</span> : null}
                                <span style={{ color: C.muted }}>{fmtD(l.trade_date)}</span>
                            </div>
                        ))}
                    </div>
                ) : null}
                {step.context && step.context.length > 0 ? (
                    <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {step.context.map((c, i) => (
                            <Badge key={i}>{c.deal_type}: {fmtN(c.broj)} · {fmtG(c.fine_g)} · {fmtEur(c.eur, true)}</Badge>
                        ))}
                    </div>
                ) : null}
                {step.note ? <div style={{ marginTop: 4, fontSize: 11, color: C.soft }}>{step.note}</div> : null}
            </div>
        </div>
    );
}

function DetailDrawer({ token, locale, target, onClose }: { token: string | null; locale: Locale; target: { type: TxnType; id: number }; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const [data, setData] = useState<DetailResp | null>(null);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        setData(null); setErr(null);
        api<DetailResp>(`/ado-1111/detail?type=${target.type}&id=${target.id}`, token)
            .then((d) => { if (alive) setData(d); })
            .catch((e: Error) => { if (alive) setErr(e.message); });
        return () => { alive = false; };
    }, [token, target.type, target.id]);

    const m = TYPE_META[target.type];
    return (
        <>
            <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.32)', zIndex: 4400 }} />
            <div style={{
                position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(520px, 92vw)', background: C.surface,
                zIndex: 4401, boxShadow: '-12px 0 40px rgba(17,24,39,0.14)', display: 'flex', flexDirection: 'column',
            }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <TypeDot type={target.type} size={10} />
                    <div style={{ fontWeight: 700, fontSize: 15, color: m.color }}>{locale === 'hr' ? m.hr : m.en}</div>
                    <button onClick={onClose} style={{ ...S.btn, marginLeft: 'auto', padding: '4px 10px' }}>✕</button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
                    {err ? <div style={{ color: C.bad, fontSize: 13 }}>{err}</div> : null}
                    {!data && !err ? <Spinner label={t('Učitavam detalj…', 'Loading detail…')} /> : null}
                    {data ? (
                        <>
                            <div style={{ ...S.card, marginBottom: 18, background: m.bg, borderColor: m.border }}>
                                <div style={{ fontSize: 12, color: C.muted, marginBottom: 2 }}>{data.row.ref} · {fmtDT(data.row.ts)}</div>
                                <div style={{ fontSize: 15, fontWeight: 700 }}>{data.row.artikl ?? '-'}</div>
                                <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
                                    {data.row.branch} · {data.row.country}{data.row.subregion !== '-' ? ' · ' + data.row.subregion : ''}
                                    {data.row.partner ? ' · ' + data.row.partner : ''}
                                </div>
                                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
                                    <span><b>{fmtEur(data.row.eur)}</b></span>
                                    <span>{data.row.fine_g !== null ? fmtG(data.row.fine_g) : '-'}</span>
                                    <span>{data.row.eur_per_g !== null ? fmtEurG(data.row.eur_per_g) : '-'}</span>
                                    <Badge>{data.row.status}</Badge>
                                    <Badge>{sourceLabel(data.row.source, locale)}</Badge>
                                </div>
                                {data.row.accepted_ts ? (
                                    <div style={{ marginTop: 6, fontSize: 12, color: C.muted }}>{t('Prihvaćena', 'Accepted')}: {fmtDT(data.row.accepted_ts)}</div>
                                ) : null}
                            </div>

                            <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
                                {t('Cjenovni lanac', 'Price chain')}
                            </div>
                            {data.chain.map((step) => <ChainStepView key={step.key} step={step} locale={locale} />)}

                            {data.field_sources ? (
                                <div style={{ marginTop: 8 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                                        {t('Provenijencija polja', 'Field provenance')}
                                    </div>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {Object.entries(data.field_sources).map(([k, v]) => (
                                            <Badge key={k}>{k}: {String(v)}</Badge>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </>
                    ) : null}
                </div>
            </div>
        </>
    );
}

// ------------------------------------------------------------------- feed tab

function FeedTab({ token, locale, filters, setFilters, qs }: {
    token: string | null; locale: Locale; filters: Filters; setFilters: (f: Filters) => void; qs: string;
}) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const [summary, setSummary] = useState<SummaryResp | null>(null);
    const [rows, setRows] = useState<FeedRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [detail, setDetail] = useState<{ type: TxnType; id: number } | null>(null);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true); setErr(null); setPage(1);
        Promise.all([
            api<SummaryResp>(`/ado-1111/summary?${qs}`, token),
            api<FeedResp>(`/ado-1111/feed?${qs}&page=1&per_page=60`, token),
        ]).then(([s, f]) => {
            if (!alive) return;
            setSummary(s); setRows(f.rows); setTotal(f.total);
        }).catch((e: Error) => { if (alive) setErr(e.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token, qs]);

    const loadMore = useCallback(() => {
        const next = page + 1;
        setLoading(true);
        api<FeedResp>(`/ado-1111/feed?${qs}&page=${next}&per_page=60`, token)
            .then((f) => { setRows((prev) => [...prev, ...f.rows]); setPage(next); setTotal(f.total); })
            .catch((e: Error) => setErr(e.message))
            .finally(() => setLoading(false));
    }, [token, qs, page]);

    const toggleType = (ty: TxnType) => {
        const has = filters.types.includes(ty);
        const next = has ? filters.types.filter((x) => x !== ty) : [...filters.types, ty];
        setFilters({ ...filters, types: next.length === 0 ? [...ALL_TYPES] : next });
    };

    return (
        <div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
                {ALL_TYPES.map((ty) => (
                    <KpiCard key={ty} type={ty} locale={locale} cell={summary ? summary[ty] : null} active={filters.types.includes(ty)} onToggle={() => toggleType(ty)} />
                ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 13, color: C.muted }}>
                    {t('Prikazano', 'Showing')} <b style={{ color: C.text }}>{fmtN(rows.length)}</b> {t('od', 'of')} <b style={{ color: C.text }}>{fmtN(total)}</b>
                </div>
                <CacheNote info={summary?._cache} locale={locale} />
                <button
                    style={{ ...S.btn, marginLeft: 'auto' }}
                    disabled={exporting}
                    onClick={() => {
                        setExporting(true);
                        downloadFile(`/ado-1111/export/feed?${qs}`, token, `m1111-feed-${filters.dateFrom}-${filters.dateTo}.xlsx`)
                            .catch((e: Error) => setErr(e.message))
                            .finally(() => setExporting(false));
                    }}
                >
                    {exporting ? t('Izvozim…', 'Exporting…') : t('Izvoz XLSX', 'Export XLSX')}
                </button>
            </div>

            {err ? <div style={{ color: C.bad, fontSize: 13, marginBottom: 10 }}>{err}</div> : null}
            <div style={{ display: 'grid', gap: 8 }}>
                {rows.map((r) => (
                    <FeedRowCard key={r.type + ':' + r.id} row={r} locale={locale} onOpen={() => setDetail({ type: r.type, id: r.id })} />
                ))}
            </div>
            {loading ? <Spinner label={t('Učitavam…', 'Loading…')} /> : null}
            {!loading && rows.length === 0 && !err ? (
                <div style={{ ...S.card, textAlign: 'center', color: C.muted, fontSize: 13 }}>{t('Nema transakcija za zadane filtere.', 'No transactions for these filters.')}</div>
            ) : null}
            {!loading && rows.length < total ? (
                <div style={{ textAlign: 'center', marginTop: 14 }}>
                    <button style={S.btn} onClick={loadMore}>{t('Učitaj još', 'Load more')} ({fmtN(total - rows.length)})</button>
                </div>
            ) : null}

            {detail ? <DetailDrawer token={token} locale={locale} target={detail} onClose={() => setDetail(null)} /> : null}
        </div>
    );
}

// ------------------------------------------------------------------- analitika

function keyLabel(key: string, dim: string, locale: Locale): string {
    if (dim === 'dan_u_tjednu') {
        const i = parseInt(key, 10);
        return (locale === 'hr' ? DOWS_HR : DOWS_EN)[i] ?? key;
    }
    if (dim === 'sat') return key + 'h';
    if (dim === 'tip' && (key === 'prodaja' || key === 'otkup_inv' || key === 'otkup_lom')) {
        const m = TYPE_META[key as TxnType];
        return locale === 'hr' ? m.shortHr : m.shortEn;
    }
    if (dim === 'mjesec' && key.length === 7) {
        const mi = parseInt(key.slice(5, 7), 10) - 1;
        return ((locale === 'hr' ? MONTHS_HR : MONTHS_EN)[mi] ?? key) + ' ' + key.slice(2, 4);
    }
    if (dim === 'dan') return key.slice(8, 10) + '.' + key.slice(5, 7) + '.';
    return key;
}

function AnalyticsTab({ token, locale, filters, qs }: { token: string | null; locale: Locale; filters: Filters; qs: string }) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const [dim, setDim] = useState('mjesec');
    const [metric, setMetric] = useState<MetricKey>('eur');
    const [compare, setCompare] = useState('');
    const [bFrom, setBFrom] = useState('');
    const [bTo, setBTo] = useState('');
    const [seriesMode, setSeriesMode] = useState<'tip' | 'ukupno'>('tip');
    const [data, setData] = useState<AnalyticsResp | null>(null);
    const [heat, setHeat] = useState<HeatCell[]>([]);
    const [heatMetric, setHeatMetric] = useState<'eur' | 'broj'>('eur');
    const [picked, setPicked] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);

    const extraQs = useMemo(() => {
        const p = new URLSearchParams();
        p.set('dimension', dim);
        if (compare) p.set('compare', compare);
        if (compare === 'custom' && bFrom && bTo) { p.set('b_from', bFrom); p.set('b_to', bTo); }
        return p.toString();
    }, [dim, compare, bFrom, bTo]);

    useEffect(() => {
        let alive = true;
        setLoading(true); setErr(null); setPicked(null);
        api<AnalyticsResp>(`/ado-1111/analytics?${qs}&${extraQs}`, token)
            .then((d) => { if (alive) setData(d); })
            .catch((e: Error) => { if (alive) setErr(e.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token, qs, extraQs]);

    useEffect(() => {
        let alive = true;
        api<{ rows: HeatCell[] }>(`/ado-1111/heatmap?${qs}`, token)
            .then((d) => { if (alive) setHeat(d.rows); })
            .catch(() => { /* heatmap je sekundaran */ });
        return () => { alive = false; };
    }, [token, qs]);

    const isTime = TIME_DIMS.includes(dim);
    const rows = data?.rows ?? [];
    const labels = rows.map((r) => keyLabel(r.key, dim, locale));

    const series: Series[] = useMemo(() => {
        const out: Series[] = [];
        if (seriesMode === 'tip' && dim !== 'tip') {
            for (const ty of filters.types) {
                out.push({ name: locale === 'hr' ? TYPE_META[ty].shortHr : TYPE_META[ty].shortEn, color: TYPE_META[ty].color, values: rows.map((r) => metricVal(r.a?.[ty] ?? null, metric)) });
            }
        } else {
            out.push({ name: 'Ukupno', color: UKUPNO_COLOR, values: rows.map((r) => metricVal(r.a?.ukupno ?? null, metric)) });
        }
        if (compare) {
            out.push({ name: compare === 'plan' ? 'Plan' : 'B', color: C.soft, dash: true, values: rows.map((r) => metricVal(r.b?.ukupno ?? null, metric)) });
        }
        return out;
    }, [rows, filters.types, metric, seriesMode, compare, dim, locale]);

    const pickedRow = picked !== null ? rows[picked] ?? null : null;
    const metricLabel = METRICS.find((m) => m.key === metric);

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ ...S.card, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                    <label style={S.label}>{t('Dimenzija', 'Dimension')}</label>
                    <select style={S.input} value={dim} onChange={(e) => setDim(e.target.value)}>
                        {Object.entries(DIM_LABELS).map(([k, [hr, en]]) => (
                            <option key={k} value={k}>{locale === 'hr' ? hr : en}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label style={S.label}>{t('Metrika', 'Metric')}</label>
                    <select style={S.input} value={metric} onChange={(e) => setMetric(e.target.value as MetricKey)}>
                        {METRICS.map((m) => <option key={m.key} value={m.key}>{locale === 'hr' ? m.hr : m.en}</option>)}
                    </select>
                </div>
                <div>
                    <label style={S.label}>{t('Usporedba', 'Compare')}</label>
                    <select style={S.input} value={compare} onChange={(e) => setCompare(e.target.value)}>
                        <option value="">{t('Bez usporedbe', 'None')}</option>
                        <option value="prethodno">{t('Prethodno razdoblje', 'Previous period')}</option>
                        <option value="lani">{t('Isto razdoblje lani', 'Same period last year')}</option>
                        <option value="plan">Plan</option>
                        <option value="custom">{t('Vlastito razdoblje', 'Custom range')}</option>
                    </select>
                </div>
                {compare === 'custom' ? (
                    <>
                        <div>
                            <label style={S.label}>B {t('od', 'from')}</label>
                            <input type="date" style={S.input} value={bFrom} onChange={(e) => setBFrom(e.target.value)} />
                        </div>
                        <div>
                            <label style={S.label}>B {t('do', 'to')}</label>
                            <input type="date" style={S.input} value={bTo} onChange={(e) => setBTo(e.target.value)} />
                        </div>
                    </>
                ) : null}
                <div>
                    <label style={S.label}>{t('Serije', 'Series')}</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button style={{ ...S.btn, ...(seriesMode === 'tip' ? { borderColor: C.text, fontWeight: 700 } : {}) }} onClick={() => setSeriesMode('tip')}>{t('Po tipu', 'By type')}</button>
                        <button style={{ ...S.btn, ...(seriesMode === 'ukupno' ? { borderColor: C.text, fontWeight: 700 } : {}) }} onClick={() => setSeriesMode('ukupno')}>{t('Ukupno', 'Total')}</button>
                    </div>
                </div>
                <button
                    style={{ ...S.btn, marginLeft: 'auto' }}
                    disabled={exporting}
                    onClick={() => {
                        setExporting(true);
                        downloadFile(`/ado-1111/export/analytics?${qs}&${extraQs}`, token, `m1111-analitika-${dim}.xlsx`)
                            .catch((e: Error) => setErr(e.message))
                            .finally(() => setExporting(false));
                    }}
                >
                    {exporting ? t('Izvozim…', 'Exporting…') : t('Izvoz XLSX', 'Export XLSX')}
                </button>
            </div>

            {data?._cache ? <div style={{ marginTop: -6 }}><CacheNote info={data._cache} locale={locale} /></div> : null}
            {data?.note ? <div style={{ fontSize: 12, color: C.muted, marginTop: -6 }}>{data.note}</div> : null}
            {data?.b_range ? (
                <div style={{ fontSize: 12, color: C.muted, marginTop: -6 }}>
                    A: {fmtD(data.a_range.from)} - {fmtD(data.a_range.to)} · B: {fmtD(data.b_range.from)} - {fmtD(data.b_range.to)}
                </div>
            ) : null}
            {err ? <div style={{ color: C.bad, fontSize: 13 }}>{err}</div> : null}

            <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{metricLabel ? (locale === 'hr' ? metricLabel.hr : metricLabel.en) : metric} {t('po', 'by')} {(DIM_LABELS[dim] ?? [dim, dim])[locale === 'hr' ? 0 : 1].toLowerCase()}</span>
                    <div style={{ display: 'flex', gap: 12, marginLeft: 'auto', flexWrap: 'wrap' }}>
                        {series.map((s, i) => (
                            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: C.muted }}>
                                <span style={{ width: 14, height: 3, background: s.color, borderRadius: 2, display: 'inline-block', opacity: s.dash ? 0.6 : 1 }} />
                                {s.name}
                            </span>
                        ))}
                    </div>
                </div>
                {loading ? <Spinner label={t('Učitavam…', 'Loading…')} /> : rows.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: C.muted, fontSize: 13 }}>{t('Nema podataka.', 'No data.')}</div>
                ) : isTime ? (
                    <LineChart series={series} labels={labels} onPick={setPicked} picked={picked} format={(v) => fmtMetric(v, metric)} />
                ) : (
                    <GroupedBars series={series} labels={labels} onPick={setPicked} picked={picked} format={(v) => fmtMetric(v, metric)} horizontalLabels={dim === 'sat' || dim === 'dan_u_tjednu' || dim === 'godina' || dim === 'tip'} />
                )}
                <div style={{ fontSize: 11, color: C.soft, marginTop: 6 }}>{t('Klik na točku/stupac otvara brojke.', 'Click a point/bar for numbers.')}</div>
            </div>

            {pickedRow ? (
                <div style={S.card}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <span style={{ fontSize: 14, fontWeight: 700 }}>{keyLabel(pickedRow.key, dim, locale)}</span>
                        {pickedRow.b_key ? <Badge>B: {keyLabel(pickedRow.b_key, dim, locale)}</Badge> : null}
                        <button style={{ ...S.btn, marginLeft: 'auto', padding: '3px 10px' }} onClick={() => setPicked(null)}>✕</button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                            <thead>
                                <tr>
                                    <th style={S.th}>{t('Tip', 'Type')}</th>
                                    <th style={{ ...S.th, textAlign: 'right' }}>{t('Broj', 'Count')}</th>
                                    <th style={{ ...S.th, textAlign: 'right' }}>EUR</th>
                                    <th style={{ ...S.th, textAlign: 'right' }}>{t('Fino g', 'Fine g')}</th>
                                    <th style={{ ...S.th, textAlign: 'right' }}>EUR/g</th>
                                    <th style={{ ...S.th, textAlign: 'right' }}>Spread</th>
                                    <th style={{ ...S.th, textAlign: 'right' }}>RUC</th>
                                    {compare ? <th style={{ ...S.th, textAlign: 'right' }}>B EUR</th> : null}
                                    {compare ? <th style={{ ...S.th, textAlign: 'right' }}>Δ</th> : null}
                                </tr>
                            </thead>
                            <tbody>
                                {[...ALL_TYPES, 'ukupno' as const].map((ty) => {
                                    const a = pickedRow.a?.[ty] ?? null;
                                    const b = pickedRow.b?.[ty] ?? null;
                                    if (ty !== 'ukupno' && !filters.types.includes(ty)) return null;
                                    return (
                                        <tr key={ty} style={ty === 'ukupno' ? { fontWeight: 700 } : undefined}>
                                            <td style={S.td}>
                                                {ty === 'ukupno' ? t('Ukupno', 'Total') : (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: TYPE_META[ty].color, fontWeight: 600 }}>
                                                        <TypeDot type={ty} size={7} />{locale === 'hr' ? TYPE_META[ty].shortHr : TYPE_META[ty].shortEn}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={S.tdNum}>{fmtN(a?.broj ?? null)}</td>
                                            <td style={S.tdNum}>{fmtEur(a?.eur ?? null)}</td>
                                            <td style={S.tdNum}>{fmtG(a?.fine_g ?? null)}</td>
                                            <td style={S.tdNum}>{fmtEurG(a?.eur_per_g ?? null)}</td>
                                            <td style={S.tdNum}>{fmtPct(a?.spread_pct ?? null)}</td>
                                            <td style={S.tdNum}>{fmtEur(a?.ruc_eur ?? null, true)}</td>
                                            {compare ? <td style={S.tdNum}>{fmtEur(b?.eur ?? null)}</td> : null}
                                            {compare ? <td style={S.tdNum}><DeltaBadge a={a?.eur ?? null} b={b?.eur ?? null} /></td> : null}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}

            <div style={S.card}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>{t('Tablica', 'Table')}</div>
                <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={S.th}>{(DIM_LABELS[dim] ?? [dim, dim])[locale === 'hr' ? 0 : 1]}</th>
                                {filters.types.map((ty) => (
                                    <th key={ty} style={{ ...S.th, textAlign: 'right', color: TYPE_META[ty].color }}>{locale === 'hr' ? TYPE_META[ty].shortHr : TYPE_META[ty].shortEn}</th>
                                ))}
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Ukupno', 'Total')}</th>
                                {compare ? <th style={{ ...S.th, textAlign: 'right' }}>B {t('ukupno', 'total')}</th> : null}
                                {compare ? <th style={{ ...S.th, textAlign: 'right' }}>Δ</th> : null}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={r.key + i} onClick={() => setPicked(i)} style={{ cursor: 'pointer', background: picked === i ? C.borderSoft : undefined }}>
                                    <td style={S.td}>{keyLabel(r.key, dim, locale)}</td>
                                    {filters.types.map((ty) => (
                                        <td key={ty} style={S.tdNum}>{fmtMetric(metricVal(r.a?.[ty] ?? null, metric), metric)}</td>
                                    ))}
                                    <td style={{ ...S.tdNum, fontWeight: 600 }}>{fmtMetric(metricVal(r.a?.ukupno ?? null, metric), metric)}</td>
                                    {compare ? <td style={S.tdNum}>{fmtMetric(metricVal(r.b?.ukupno ?? null, metric), metric)}</td> : null}
                                    {compare ? <td style={S.tdNum}><DeltaBadge a={metricVal(r.a?.ukupno ?? null, metric)} b={metricVal(r.b?.ukupno ?? null, metric)} /></td> : null}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{t('Kada se trguje: dan u tjednu × sat', 'When trading happens: weekday × hour')}</span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                        <button style={{ ...S.btn, padding: '4px 10px', ...(heatMetric === 'eur' ? { borderColor: C.text, fontWeight: 700 } : {}) }} onClick={() => setHeatMetric('eur')}>EUR</button>
                        <button style={{ ...S.btn, padding: '4px 10px', ...(heatMetric === 'broj' ? { borderColor: C.text, fontWeight: 700 } : {}) }} onClick={() => setHeatMetric('broj')}>{t('Broj', 'Count')}</button>
                    </div>
                </div>
                <HeatmapGrid cells={heat} metric={heatMetric} types={filters.types} locale={locale} formatEur={(v) => fmtEur(v, true)} />
            </div>
        </div>
    );
}

// ------------------------------------------------------------------- poslovnice

function BranchesTab({ token, locale, filters, qs }: { token: string | null; locale: Locale; filters: Filters; qs: string }) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const [data, setData] = useState<BranchCmpRow[]>([]);
    const [cacheInfo, setCacheInfo] = useState<CacheInfo | null>(null);
    const [metric, setMetric] = useState<MetricKey>('eur');
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [open, setOpen] = useState<string | null>(null);
    const [limit, setLimit] = useState(25);

    useEffect(() => {
        let alive = true;
        setLoading(true); setErr(null); setOpen(null); setLimit(25);
        api<{ rows: BranchCmpRow[]; _cache?: CacheInfo }>(`/ado-1111/branches-compare?${qs}`, token)
            .then((d) => { if (alive) { setData(d.rows); setCacheInfo(d._cache ?? null); } })
            .catch((e: Error) => { if (alive) setErr(e.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token, qs]);

    const sorted = useMemo(() => {
        const arr = [...data];
        arr.sort((a, b) => (metricVal(b.metrics.ukupno ?? null, metric) ?? 0) - (metricVal(a.metrics.ukupno ?? null, metric) ?? 0));
        return arr;
    }, [data, metric]);
    const maxV = Math.max(1, ...sorted.map((r) => Math.abs(metricVal(r.metrics.ukupno ?? null, metric) ?? 0)));
    const metricLabel = METRICS.find((m) => m.key === metric);

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ ...S.card, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                    <label style={S.label}>{t('Metrika ranga', 'Ranking metric')}</label>
                    <select style={S.input} value={metric} onChange={(e) => setMetric(e.target.value as MetricKey)}>
                        {METRICS.map((m) => <option key={m.key} value={m.key}>{locale === 'hr' ? m.hr : m.en}</option>)}
                    </select>
                </div>
                <CacheNote info={cacheInfo} locale={locale} />
                <div style={{ fontSize: 12, color: C.muted, paddingBottom: 8 }}>
                    {t('Poslovnica ukupno', 'Total branches')}: <b style={{ color: C.text }}>{fmtN(sorted.length)}</b> · {t('klik na red otvara brojke po tipu', 'click a row for per-type numbers')}
                </div>
            </div>

            {err ? <div style={{ color: C.bad, fontSize: 13 }}>{err}</div> : null}
            {loading ? <Spinner label={t('Učitavam…', 'Loading…')} /> : null}

            <div style={{ display: 'grid', gap: 6 }}>
                {sorted.slice(0, limit).map((r, i) => {
                    const v = metricVal(r.metrics.ukupno ?? null, metric) ?? 0;
                    const isOpen = open === r.branch;
                    return (
                        <div key={r.branch} style={{ background: C.surface, border: `1px solid ${isOpen ? C.muted : C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                            <div
                                onClick={() => setOpen(isOpen ? null : r.branch)}
                                style={{ display: 'grid', gridTemplateColumns: '34px 1.4fr 2fr 130px 130px', gap: 12, alignItems: 'center', padding: '10px 14px', cursor: 'pointer' }}
                            >
                                <span style={{ fontSize: 12, fontWeight: 700, color: C.soft, fontVariantNumeric: 'tabular-nums' }}>{i + 1}.</span>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.branch}</div>
                                    <div style={{ fontSize: 11, color: C.muted }}>{r.country}{r.subregion !== '-' ? ' · ' + r.subregion : ''} · {r.group}</div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ flex: 1, height: 16, background: C.borderSoft, borderRadius: 6, overflow: 'hidden' }}>
                                        <div style={{ width: `${Math.max(1.5, (Math.abs(v) / maxV) * 100)}%`, height: '100%', background: v >= 0 ? UKUPNO_COLOR : C.bad, opacity: 0.85, borderRadius: 6 }} />
                                    </div>
                                    <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 90, textAlign: 'right' }}>{fmtMetric(v, metric)}</span>
                                </div>
                                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                    {ALL_TYPES.filter((ty) => filters.types.includes(ty)).map((ty) => (
                                        <span key={ty} title={locale === 'hr' ? TYPE_META[ty].hr : TYPE_META[ty].en} style={{ fontSize: 11, fontWeight: 600, color: TYPE_META[ty].color, background: TYPE_META[ty].bg, borderRadius: 5, padding: '2px 6px', fontVariantNumeric: 'tabular-nums' }}>
                                            {fmtMetric(metricVal(r.metrics[ty] ?? null, metric), metric)}
                                        </span>
                                    ))}
                                </div>
                                <div style={{ justifySelf: 'end' }}>
                                    <Spark points={r.trend.map((p) => p.eur)} color={C.muted} />
                                </div>
                            </div>
                            {isOpen ? (
                                <div style={{ borderTop: `1px solid ${C.borderSoft}`, padding: '10px 14px', overflowX: 'auto' }}>
                                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                        <thead>
                                            <tr>
                                                <th style={S.th}>{t('Tip', 'Type')}</th>
                                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Broj', 'Count')}</th>
                                                <th style={{ ...S.th, textAlign: 'right' }}>EUR</th>
                                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Fino g', 'Fine g')}</th>
                                                <th style={{ ...S.th, textAlign: 'right' }}>EUR/g</th>
                                                <th style={{ ...S.th, textAlign: 'right' }}>Spot</th>
                                                <th style={{ ...S.th, textAlign: 'right' }}>Spread</th>
                                                <th style={{ ...S.th, textAlign: 'right' }}>RUC</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...ALL_TYPES, 'ukupno' as const].map((ty) => {
                                                const c = r.metrics[ty] ?? null;
                                                if (ty !== 'ukupno' && !filters.types.includes(ty)) return null;
                                                return (
                                                    <tr key={ty} style={ty === 'ukupno' ? { fontWeight: 700 } : undefined}>
                                                        <td style={S.td}>
                                                            {ty === 'ukupno' ? t('Ukupno', 'Total') : (
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: TYPE_META[ty].color, fontWeight: 600 }}>
                                                                    <TypeDot type={ty} size={7} />{locale === 'hr' ? TYPE_META[ty].shortHr : TYPE_META[ty].shortEn}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td style={S.tdNum}>{fmtN(c?.broj ?? null)}</td>
                                                        <td style={S.tdNum}>{fmtEur(c?.eur ?? null)}</td>
                                                        <td style={S.tdNum}>{fmtG(c?.fine_g ?? null)}</td>
                                                        <td style={S.tdNum}>{fmtEurG(c?.eur_per_g ?? null)}</td>
                                                        <td style={S.tdNum}>{fmtEurG(c?.spot_eur_g ?? null)}</td>
                                                        <td style={S.tdNum}>{fmtPct(c?.spread_pct ?? null)}</td>
                                                        <td style={S.tdNum}>{fmtEur(c?.ruc_eur ?? null, true)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {r.trend.length > 1 ? (
                                        <div style={{ marginTop: 10 }}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{t('Mjesečni trend (EUR)', 'Monthly trend (EUR)')}</div>
                                            <LineChart height={160} labels={r.trend.map((p) => keyLabel(p.month, 'mjesec', locale))} series={[{ name: r.branch, color: UKUPNO_COLOR, values: r.trend.map((p) => p.eur) }]} format={(v) => fmtEur(v, true)} />
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
            {!loading && sorted.length > limit ? (
                <div style={{ textAlign: 'center' }}>
                    <button style={S.btn} onClick={() => setLimit(limit + 25)}>{t('Prikaži još', 'Show more')} ({fmtN(sorted.length - limit)})</button>
                </div>
            ) : null}
            {!loading && sorted.length === 0 && !err ? (
                <div style={{ ...S.card, textAlign: 'center', color: C.muted, fontSize: 13 }}>{t('Nema podataka za zadane filtere.', 'No data for these filters.')}</div>
            ) : null}
            <div style={{ fontSize: 11, color: C.soft }}>
                {t('Rang po', 'Ranked by')} {metricLabel ? (locale === 'hr' ? metricLabel.hr : metricLabel.en).toLowerCase() : metric} · {t('mini graf = mjesečni EUR trend', 'sparkline = monthly EUR trend')}
            </div>
        </div>
    );
}

// ------------------------------------------------------------------- karta

function MapTab({ token, locale, filters, setFilters, qs }: {
    token: string | null; locale: Locale; filters: Filters; setFilters: (f: Filters) => void; qs: string;
}) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const [data, setData] = useState<MapResp | null>(null);
    const [metric, setMetric] = useState<MetricKey>('eur');
    const [sel, setSel] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        setLoading(true); setErr(null);
        api<MapResp>(`/ado-1111/map?${qs}`, token)
            .then((d) => { if (alive) setData(d); })
            .catch((e: Error) => { if (alive) setErr(e.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token, qs]);

    const activeTypes = useMemo(() => ALL_TYPES.filter((ty) => filters.types.includes(ty)), [filters.types]);
    const typeDefs = useMemo(
        () => activeTypes.map((ty) => ({ key: ty, name: locale === 'hr' ? TYPE_META[ty].shortHr : TYPE_META[ty].shortEn, color: TYPE_META[ty].color })),
        [activeTypes, locale],
    );
    const points = useMemo<GeoPoint[]>(
        () => (data?.points ?? [])
            .filter((p): p is MapPointRow & { lat: number; lng: number } => p.lat !== null && p.lng !== null)
            .map((p) => ({
                branch: p.branch, lat: p.lat, lng: p.lng,
                perType: Object.fromEntries(activeTypes.map((ty) => [ty, metricVal(p.metrics[ty] ?? null, metric)])),
            })),
        [data, activeTypes, metric],
    );
    const fmt = useCallback((v: number) => fmtMetric(v, metric), [metric]);
    const onSelection = useCallback((branches: string[]) => setSel(branches), []);
    const onPoint = useCallback((branch: string) => setSel((cur) => (cur.length === 1 && cur[0] === branch ? [] : [branch])), []);
    const metricLabel = METRICS.find((m) => m.key === metric);

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ ...S.card, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                    <label style={S.label}>{t('Metrika (veličina točke)', 'Metric (dot size)')}</label>
                    <select style={S.input} value={metric} onChange={(e) => setMetric(e.target.value as MetricKey)}>
                        {METRICS.map((m) => <option key={m.key} value={m.key}>{locale === 'hr' ? m.hr : m.en}</option>)}
                    </select>
                </div>
                <CacheNote info={data?._cache} locale={locale} />
                <div style={{ fontSize: 12, color: C.muted, paddingBottom: 8 }}>
                    {t('Okvir ili laso u alatnoj traci karte označava poslovnice; kotačić zumira, povlačenje pomiče.',
                        'Rect or lasso in the map toolbox selects branches; wheel zooms, drag pans.')}
                </div>
                {filters.branches.length > 0 ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 6 }}>
                        <span style={{ fontSize: 12, color: C.muted }}>
                            {t('Aktivan filter poslovnica', 'Branch filter active')}: <b style={{ color: C.text }}>{fmtN(filters.branches.length)}</b>
                        </span>
                        <button style={{ ...S.btn, padding: '5px 10px', fontSize: 12 }} onClick={() => setFilters({ ...filters, branches: [] })}>
                            {t('Ukloni', 'Clear')}
                        </button>
                    </div>
                ) : null}
            </div>

            {err ? <div style={{ color: C.bad, fontSize: 13 }}>{err}</div> : null}
            {loading ? <Spinner label={t('Učitavam…', 'Loading…')} /> : null}

            <div style={{ ...S.card, padding: 0, position: 'relative', overflow: 'hidden' }}>
                <GeoScatterMap
                    points={points}
                    types={typeDefs}
                    height={Math.max(480, Math.min(760, window.innerHeight - 320))}
                    format={fmt}
                    onSelectionChange={onSelection}
                    onPointClick={onPoint}
                />
                {sel.length > 0 ? (
                    <div style={{
                        position: 'absolute', left: 14, bottom: 14, background: C.surface, border: `1px solid ${C.border}`,
                        borderRadius: 10, padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'center',
                        boxShadow: '0 10px 30px rgba(17, 24, 39, 0.14)', maxWidth: '70%',
                    }}>
                        <span style={{ fontSize: 13 }}>
                            <b>{fmtN(sel.length)}</b> {t('poslovnica označeno', 'branches selected')}
                            <span style={{ color: C.muted }}> · {sel.slice(0, 4).join(', ')}{sel.length > 4 ? '…' : ''}</span>
                        </span>
                        <button style={{ ...S.btnPrimary, padding: '6px 12px', fontSize: 12 }} onClick={() => { setFilters({ ...filters, branches: sel }); setSel([]); }}>
                            {t('Primijeni kao filter', 'Apply as filter')}
                        </button>
                        <button style={{ ...S.btn, padding: '6px 12px', fontSize: 12 }} onClick={() => setSel([])}>{t('Poništi', 'Cancel')}</button>
                    </div>
                ) : null}
                <div style={{ position: 'absolute', right: 14, bottom: 14, display: 'flex', gap: 10, background: 'rgba(255,255,255,0.9)', border: `1px solid ${C.borderSoft}`, borderRadius: 8, padding: '6px 10px' }}>
                    {typeDefs.map((ty) => (
                        <span key={ty.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: ty.color }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: ty.color }} />{ty.name}
                        </span>
                    ))}
                </div>
            </div>

            {data && data.unplaced.length > 0 ? (
                <div style={{ ...S.card, fontSize: 12, color: C.muted }}>
                    {t('Bez koordinata (nisu na karti, jesu u brojkama)', 'No coordinates (not on the map, included in totals)')}:{' '}
                    {data.unplaced.map((p) => p.branch).join(' · ')}
                </div>
            ) : null}
            {!loading && points.length === 0 && !err ? (
                <div style={{ ...S.card, textAlign: 'center', color: C.muted, fontSize: 13 }}>{t('Nema podataka za zadane filtere.', 'No data for these filters.')}</div>
            ) : null}
            <div style={{ fontSize: 11, color: C.soft }}>
                {t('Veličina točke', 'Dot size')}: {metricLabel ? (locale === 'hr' ? metricLabel.hr : metricLabel.en).toLowerCase() : metric} · {t('boja = tip transakcije · klik na točku označi poslovnicu', 'colour = transaction type · click a dot to select a branch')}
            </div>
        </div>
    );
}

// ------------------------------------------------------------------- plan

type PlanGrid = Record<number, { promet_eur: string; fine_g: string; ruc_eur: string; broj: string; note: string }>;
function emptyGrid(): PlanGrid {
    const g: PlanGrid = {};
    for (let m = 1; m <= 12; m++) g[m] = { promet_eur: '', fine_g: '', ruc_eur: '', broj: '', note: '' };
    return g;
}

function PlanTab({ token, locale, meta }: { token: string | null; locale: Locale; meta: MetaResp | null }) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const nowYear = new Date().getFullYear();
    const years = useMemo(() => {
        const set = new Set<number>([nowYear - 1, nowYear, nowYear + 1, ...(meta?.plan_years ?? [])]);
        return [...set].sort();
    }, [meta, nowYear]);

    const [year, setYear] = useState(nowYear);
    const [type, setType] = useState<TxnType>('prodaja');
    const [branch, setBranch] = useState(''); // '' = sve poslovnice (mrezni plan)
    const [grid, setGrid] = useState<PlanGrid>(emptyGrid());
    const [pva, setPva] = useState<PvaResp | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [pvaMetric, setPvaMetric] = useState<'eur' | 'ruc_eur' | 'fine_g' | 'broj'>('eur');

    const loadAll = useCallback(() => {
        let alive = true;
        setLoading(true); setErr(null); setMsg(null);
        Promise.all([
            api<{ year: number; rows: PlanApiRow[] }>(`/ado-1111/plans?year=${year}`, token),
            api<PvaResp>(`/ado-1111/plan-vs-actual?year=${year}&txn_type=${type}${branch ? '&branch=' + encodeURIComponent(branch) : ''}`, token),
        ]).then(([p, v]) => {
            if (!alive) return;
            const g = emptyGrid();
            for (const r of p.rows) {
                if (r.txn_type !== type) continue;
                if ((r.branch ?? '') !== branch) continue;
                g[r.month] = {
                    promet_eur: r.promet_eur !== null ? String(r.promet_eur) : '',
                    fine_g: r.fine_g !== null ? String(r.fine_g) : '',
                    ruc_eur: r.ruc_eur !== null ? String(r.ruc_eur) : '',
                    broj: r.broj !== null ? String(r.broj) : '',
                    note: r.note ?? '',
                };
            }
            setGrid(g); setPva(v);
        }).catch((e: Error) => { if (alive) setErr(e.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token, year, type, branch]);

    useEffect(() => loadAll(), [loadAll]);

    const save = () => {
        setSaving(true); setErr(null); setMsg(null);
        const rows = Object.entries(grid).map(([m, v]) => ({
            year, month: Number(m), branch: branch || null, txn_type: type,
            promet_eur: v.promet_eur.trim() === '' ? null : Number(v.promet_eur.replace(',', '.')),
            fine_g: v.fine_g.trim() === '' ? null : Number(v.fine_g.replace(',', '.')),
            ruc_eur: v.ruc_eur.trim() === '' ? null : Number(v.ruc_eur.replace(',', '.')),
            broj: v.broj.trim() === '' ? null : Number(v.broj),
            note: v.note.trim() === '' ? null : v.note.trim(),
        }));
        mutate<{ saved: number }>('/ado-1111/plans', token, 'POST', { rows })
            .then((r) => { setMsg(t(`Spremljeno ${r.saved} ćelija.`, `Saved ${r.saved} cells.`)); loadAll(); })
            .catch((e: Error) => setErr(e.message))
            .finally(() => setSaving(false));
    };

    const setCell = (m: number, field: keyof PlanGrid[number], val: string) => {
        setGrid((g) => ({ ...g, [m]: { ...g[m], [field]: val } }));
    };

    const months = locale === 'hr' ? MONTHS_HR : MONTHS_EN;
    const pvaRows = pva?.rows ?? [];
    const pvaVal = (side: 'actual' | 'plan', r: PvaRow): number | null => {
        const o = r[side];
        if (!o) return null;
        return pvaMetric === 'eur' ? o.eur : pvaMetric === 'ruc_eur' ? o.ruc_eur : pvaMetric === 'fine_g' ? o.fine_g : o.broj;
    };
    const fmtPva = (v: number | null) => (pvaMetric === 'broj' ? fmtN(v) : pvaMetric === 'fine_g' ? fmtG(v) : fmtEur(v, true));

    const inputStyle: CSSProperties = { ...S.input, width: '100%', padding: '5px 8px', fontSize: 12, textAlign: 'right' };
    const tm = TYPE_META[type];

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ ...S.card, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                    <label style={S.label}>{t('Godina', 'Year')}</label>
                    <select style={S.input} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <div>
                    <label style={S.label}>{t('Tip', 'Type')}</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                        {ALL_TYPES.map((ty) => <TypeChip key={ty} type={ty} active={type === ty} onClick={() => setType(ty)} locale={locale} />)}
                    </div>
                </div>
                <div>
                    <label style={S.label}>{t('Poslovnica', 'Branch')}</label>
                    <select style={{ ...S.input, maxWidth: 260 }} value={branch} onChange={(e) => setBranch(e.target.value)}>
                        <option value="">{t('Sve poslovnice (mreža)', 'All branches (network)')}</option>
                        {(meta?.branches ?? []).map((b) => <option key={b.branch} value={b.branch}>{b.branch}</option>)}
                    </select>
                </div>
                <button style={{ ...S.btnPrimary, marginLeft: 'auto' }} disabled={saving || loading} onClick={save}>
                    {saving ? t('Spremam…', 'Saving…') : t('Spremi plan', 'Save plan')}
                </button>
            </div>

            {msg ? <div style={{ fontSize: 13, color: C.good }}>{msg}</div> : null}
            {err ? <div style={{ fontSize: 13, color: C.bad }}>{err}</div> : null}

            <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <TypeDot type={type} size={9} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: tm.color }}>{t('Plan', 'Plan')} {year} · {locale === 'hr' ? tm.hr : tm.en} · {branch || t('mreža', 'network')}</span>
                    {loading ? <span style={{ fontSize: 12, color: C.muted }}>{t('učitavam…', 'loading…')}</span> : null}
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720 }}>
                        <thead>
                            <tr>
                                <th style={S.th}>{t('Mjesec', 'Month')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Promet EUR', 'Turnover EUR')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Fino g', 'Fine g')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>RUC EUR</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Broj', 'Count')}</th>
                                <th style={S.th}>{t('Bilješka', 'Note')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                <tr key={m}>
                                    <td style={{ ...S.td, fontWeight: 600 }}>{months[m - 1]}</td>
                                    <td style={{ ...S.td, width: 130 }}><input style={inputStyle} inputMode="decimal" value={grid[m].promet_eur} onChange={(e) => setCell(m, 'promet_eur', e.target.value)} /></td>
                                    <td style={{ ...S.td, width: 120 }}><input style={inputStyle} inputMode="decimal" value={grid[m].fine_g} onChange={(e) => setCell(m, 'fine_g', e.target.value)} /></td>
                                    <td style={{ ...S.td, width: 120 }}><input style={inputStyle} inputMode="decimal" value={grid[m].ruc_eur} onChange={(e) => setCell(m, 'ruc_eur', e.target.value)} /></td>
                                    <td style={{ ...S.td, width: 90 }}><input style={inputStyle} inputMode="numeric" value={grid[m].broj} onChange={(e) => setCell(m, 'broj', e.target.value)} /></td>
                                    <td style={S.td}><input style={{ ...inputStyle, textAlign: 'left' }} value={grid[m].note} onChange={(e) => setCell(m, 'note', e.target.value)} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div style={{ fontSize: 11, color: C.soft, marginTop: 8 }}>
                    {t('Prazna ćelija briše plan za taj mjesec. Decimalni zapis: točka ili zarez.', 'An empty cell deletes the plan for that month. Decimals: dot or comma.')}
                </div>
            </div>

            <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{t('Plan vs realizirano', 'Plan vs actual')} {year}</span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                        {([['eur', 'EUR'], ['ruc_eur', 'RUC'], ['fine_g', t('Fino g', 'Fine g')], ['broj', t('Broj', 'Count')]] as [typeof pvaMetric, string][]).map(([k, lb]) => (
                            <button key={k} style={{ ...S.btn, padding: '4px 10px', ...(pvaMetric === k ? { borderColor: C.text, fontWeight: 700 } : {}) }} onClick={() => setPvaMetric(k)}>{lb}</button>
                        ))}
                    </div>
                </div>
                <GroupedBars
                    height={240}
                    horizontalLabels
                    format={fmtPva}
                    labels={months}
                    series={[
                        { name: t('Realizirano', 'Actual'), color: tm.color, values: pvaRows.map((r) => pvaVal('actual', r)) },
                        { name: 'Plan', color: C.soft, dash: true, values: pvaRows.map((r) => pvaVal('plan', r)) },
                    ]}
                />
                <div style={{ overflowX: 'auto', marginTop: 8 }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={S.th}>{t('Mjesec', 'Month')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Realizirano', 'Actual')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>Plan</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Ostvarenje', 'Attainment')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>Δ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pvaRows.map((r) => {
                                const a = pvaVal('actual', r);
                                const p = pvaVal('plan', r);
                                const att = a !== null && p !== null && p !== 0 ? (100 * a) / p : null;
                                return (
                                    <tr key={r.month}>
                                        <td style={S.td}>{months[r.month - 1]}</td>
                                        <td style={S.tdNum}>{fmtPva(a)}</td>
                                        <td style={S.tdNum}>{fmtPva(p)}</td>
                                        <td style={{ ...S.tdNum, fontWeight: 600, color: att === null ? C.soft : att >= 100 ? C.good : att >= 80 ? '#b45309' : C.bad }}>
                                            {att !== null ? nf0.format(att) + ' %' : '-'}
                                        </td>
                                        <td style={S.tdNum}>{a !== null && p !== null ? fmtPva(a - p) : '-'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------ pregled

/** Promjena vs LY: strelica + postotak, zeleno/crveno. */
function DeltaPct({ pct }: { pct: number | null }) {
    if (pct === null || pct === undefined) return <span style={{ color: C.soft, fontSize: 12 }}>-</span>;
    const up = pct >= 0;
    return (
        <span style={{ fontSize: 12, fontWeight: 700, color: up ? C.good : C.bad, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {up ? '▲' : '▼'} {nfG.format(Math.abs(pct))} %
        </span>
    );
}

/** Kartica jednog vremenskog prozora: TX / EUR / RUC, tekuce vs LY. */
function PregledCard({ p, title, locale }: { p: PregledPeriod; title: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const sign = (v: number) => (v >= 0 ? '+' : '');
    const rows: { label: string; cur: string; ly: string; diff: string; pct: number | null }[] = [
        { label: t('Transakcije', 'Transactions'), cur: fmtN(p.tx), ly: fmtN(p.tx_ly), diff: sign(p.tx_diff) + fmtN(p.tx_diff), pct: p.tx_pct },
        { label: 'EUR', cur: fmtEur(p.eur, true), ly: fmtEur(p.eur_ly, true), diff: sign(p.eur_diff) + fmtEur(p.eur_diff, true), pct: p.eur_pct },
        {
            label: 'RUC',
            cur: p.ruc !== null ? fmtEur(p.ruc, true) : '-',
            ly: p.ruc_ly !== null ? fmtEur(p.ruc_ly, true) : '-',
            diff: p.ruc_diff !== null ? sign(p.ruc_diff) + fmtEur(p.ruc_diff, true) : '-',
            pct: p.ruc_pct,
        },
    ];
    return (
        <div style={{ ...S.card, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: C.muted }}>{title}</div>
            <div style={{ fontSize: 11, color: C.soft, marginTop: 2 }}>
                {fmtD(p.range.from)} — {fmtD(p.range.to)} <span style={{ color: C.border }}>|</span> LY {fmtD(p.range_ly.from)} — {fmtD(p.range_ly.to)}
            </div>
            <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 8 }}>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.label}>
                            <td style={{ ...S.td, borderBottom: 'none', padding: '4px 0', color: C.muted, fontSize: 12 }}>{r.label}</td>
                            <td style={{ ...S.tdNum, borderBottom: 'none', padding: '4px 0', fontSize: 15, fontWeight: 700 }}>{r.cur}</td>
                            <td style={{ ...S.tdNum, borderBottom: 'none', padding: '4px 0 4px 10px', color: C.soft, fontSize: 12 }}>{r.ly}</td>
                            <td style={{ ...S.tdNum, borderBottom: 'none', padding: '4px 0 4px 10px', fontSize: 12 }}>{r.diff}</td>
                            <td style={{ ...S.tdNum, borderBottom: 'none', padding: '4px 0 4px 10px' }}><DeltaPct pct={r.pct} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {p.ruc === null && p.tx > 0 ? (
                <div style={{ fontSize: 10, color: C.soft, marginTop: 4 }}>{t('RUC nedostupan (nema živog feeda u razdoblju)', 'RUC unavailable (no live feed in period)')}</div>
            ) : p.ruc !== null && p.ruc_n < p.tx ? (
                <div style={{ fontSize: 10, color: C.soft, marginTop: 4 }}>{t('RUC pokriva', 'RUC covers')} {fmtN(p.ruc_n)}/{fmtN(p.tx)} tx</div>
            ) : null}
        </div>
    );
}

/** Segmentacijska tablica (po iznosu ili po kupcu), tekuce razdoblje vs LY. */
function PregledSegTable({ title, seg, locale }: { title: string; seg: PregledSegments; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    return (
        <div style={S.card}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{title}</span>
                <span style={{ fontSize: 11, color: C.soft }}>
                    {fmtD(seg.range.from)} — {fmtD(seg.range.to)} vs {fmtD(seg.range_ly.from)} — {fmtD(seg.range_ly.to)}
                </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                        <tr>
                            <th style={S.th}>{t('Segment', 'Segment')}</th>
                            <th style={{ ...S.th, textAlign: 'right' }}>TX</th>
                            <th style={{ ...S.th, textAlign: 'right' }}>TX LY</th>
                            <th style={{ ...S.th, textAlign: 'right' }}>Δ %</th>
                            <th style={{ ...S.th, textAlign: 'right' }}>EUR</th>
                            <th style={{ ...S.th, textAlign: 'right' }}>EUR LY</th>
                            <th style={{ ...S.th, textAlign: 'right' }}>Δ %</th>
                            <th style={{ ...S.th, textAlign: 'right' }}>RUC</th>
                            <th style={{ ...S.th, textAlign: 'right' }}>RUC LY</th>
                        </tr>
                    </thead>
                    <tbody>
                        {seg.buckets.map((b) => (
                            <tr key={b.key ?? b.label}>
                                <td style={{ ...S.td, fontWeight: 600 }}>{b.label}</td>
                                <td style={S.tdNum}>{fmtN(b.tx)}</td>
                                <td style={{ ...S.tdNum, color: C.soft }}>{fmtN(b.tx_ly)}</td>
                                <td style={S.tdNum}><DeltaPct pct={b.tx_pct} /></td>
                                <td style={S.tdNum}>{fmtEur(b.eur, true)}</td>
                                <td style={{ ...S.tdNum, color: C.soft }}>{fmtEur(b.eur_ly, true)}</td>
                                <td style={S.tdNum}><DeltaPct pct={b.eur_pct} /></td>
                                <td style={S.tdNum}>{b.ruc !== null ? fmtEur(b.ruc, true) : '-'}</td>
                                <td style={{ ...S.tdNum, color: C.soft }}>{b.ruc_ly !== null ? fmtEur(b.ruc_ly, true) : '-'}</td>
                            </tr>
                        ))}
                        {seg.buckets.length === 0 ? <tr><td style={S.td} colSpan={9}>{t('Nema podataka.', 'No data.')}</td></tr> : null}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/**
 * Pregled (po uzoru na dnevni segmentacijski izvjestaj modula 1.4): dan /
 * tjedan(-364) / mjesec MTD / odabrano razdoblje vs prosla godina, s TX, EUR
 * i RUC-om; segmenti po iznosu i po rednom broju kupnje kupca; trend cijene.
 * Prodaja investicijskog zlata, trenutak prihvata ponude = trenutak prodaje.
 */
function PregledTab({ token, locale, qs }: { token: string | null; locale: Locale; qs: string }) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const [data, setData] = useState<PregledResp | null>(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        setLoading(true); setErr(null);
        api<PregledResp>(`/ado-1111/pregled?${qs}`, token)
            .then((d) => { if (alive) setData(d); })
            .catch((e: Error) => { if (alive) setErr(e.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token, qs]);

    if (err) return <div style={{ ...S.card, color: C.bad }}>{err}</div>;
    if (loading || !data) return <Spinner label={t('Učitavam pregled…', 'Loading overview…')} />;

    const order: ('day' | 'week' | 'month' | 'custom')[] = ['day', 'week', 'month', 'custom'];
    const titles: Record<typeof order[number], string> = {
        day: t('Dan', 'Day'),
        week: t('Tjedan (od ponedjeljka)', 'Week (from Monday)'),
        month: t('Mjesec (od 1.)', 'Month to date'),
        custom: t('Odabrano razdoblje', 'Selected range'),
    };
    const pm = TYPE_META.prodaja;
    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{t('Prodaja investicijskog zlata vs prošla godina', 'Investment gold sales vs last year')}</span>
                <Badge color={pm.color} bg={pm.bg}>{t('trenutak prodaje = prihvat ponude', 'sale moment = offer acceptance')}</Badge>
                <span style={{ marginLeft: 'auto' }}><CacheNote info={data._cache} locale={locale} /></span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
                {order.map((k) => <PregledCard key={k} p={data.periods[k]} title={titles[k]} locale={locale} />)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(460px, 1fr))', gap: 16 }}>
                <PregledSegTable title={t('Segmenti po iznosu transakcije', 'Segments by transaction amount')} seg={data.amount_segments} locale={locale} />
                <PregledSegTable title={t('Segmenti kupaca (redni broj kupnje, mrežno)', 'Customer segments (purchase ordinal, network-wide)')} seg={data.customer_segments} locale={locale} />
            </div>
            {(['gold', 'silver'] as const).map((m) => {
                const pt = data.price_trend[m];
                if (!pt.available || pt.series.length === 0) return null;
                return (
                    <div key={m} style={S.card}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
                            <span style={{ fontSize: 14, fontWeight: 700 }}>
                                {m === 'gold' ? t('Cijena zlata (30 dana)', 'Gold price (30 days)') : t('Cijena srebra (30 dana)', 'Silver price (30 days)')}
                            </span>
                            <span style={{ fontSize: 12, color: C.soft }}>{pt.unit}</span>
                            <DeltaPct pct={pt.change_pct} />
                        </div>
                        <LineChart
                            height={220}
                            labels={pt.series.map((x) => x.date.slice(5))}
                            format={(v) => nf2.format(v)}
                            series={[{ name: m === 'gold' ? 'Au' : 'Ag', color: m === 'gold' ? '#b45309' : '#6b7280', values: pt.series.map((x) => x.close) }]}
                        />
                    </div>
                );
            })}
            <div style={{ fontSize: 11, color: C.soft }}>
                {data.ruc_from ? <>{t('RUC dostupan od', 'RUC available from')} {fmtD(data.ruc_from)}. </> : null}
                {data.napomena ?? ''}
            </div>
        </div>
    );
}

// ----------------------------------------------------------------- lijevak

/** Mini horizontalna traka 0-100 % (konverzija, udio). Bez SVG-a, cisti CSS. */
function PctBar({ pct, color }: { pct: number | null; color: string }) {
    if (pct === null || pct === undefined) return <span style={{ color: C.soft }}>-</span>;
    const w = Math.max(0, Math.min(100, pct));
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-block', width: 90, height: 8, background: C.borderSoft, borderRadius: 4, overflow: 'hidden' }}>
                <span style={{ display: 'block', width: `${w}%`, height: '100%', background: color }} />
            </span>
            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{nf2.format(pct)} %</span>
        </span>
    );
}

/** KPI plocica za lijevak/kanale: naslov, glavna brojka, podnaslov. */
function MiniKpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
    return (
        <div style={{ ...S.card, flex: 1, minWidth: 150, padding: '12px 16px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{value}</div>
            {sub ? <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div> : null}
        </div>
    );
}

/**
 * Prodajni lijevak: ponuda -> prihvat (predracun/racun). Trenutak prihvata je
 * poslovna okosnica (nabava zlata krece odmah po prihvatu), pa je konverzija
 * i brzina do prihvata prva stvar koju ovaj tab pokazuje.
 */
function FunnelTab({ token, locale, qs }: { token: string | null; locale: Locale; qs: string }) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const [data, setData] = useState<FunnelResp | null>(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        setLoading(true); setErr(null);
        api<FunnelResp>(`/ado-1111/funnel?${qs}`, token)
            .then((d) => { if (alive) setData(d); })
            .catch((e: Error) => { if (alive) setErr(e.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token, qs]);

    if (err) return <div style={{ ...S.card, color: C.bad }}>{err}</div>;
    if (loading || !data) return <Spinner label={t('Učitavam lijevak…', 'Loading funnel…')} />;

    const tot = data.totals;
    const pm = TYPE_META.prodaja;
    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <MiniKpi label={t('Ponude', 'Offers')} value={fmtN(tot.ponude)} sub={fmtEur(tot.ponude_eur, true)} />
                <MiniKpi label={t('Prihvaćene', 'Accepted')} value={fmtN(tot.prihvacene)} sub={fmtEur(tot.prihvaceno_eur, true)} />
                <MiniKpi label={t('Konverzija', 'Conversion')} value={tot.konverzija_pct !== null ? nf2.format(tot.konverzija_pct) + ' %' : '-'} sub={t('prihvaćeno / izdano', 'accepted / issued')} />
                <MiniKpi label={t('Predračuni', 'Proformas')} value={fmtN(tot.predracuni)} sub={t('potvrda prodaje', 'sale confirmation')} />
                <MiniKpi label={t('Računi', 'Invoices')} value={fmtN(tot.racuni)} sub={t('isporuka', 'delivery')} />
                <MiniKpi label={t('Istekle', 'Expired')} value={fmtN(tot.istekle)} />
            </div>

            <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{t('Lijevak po mjesecu izdavanja ponude', 'Funnel by offer month')}</span>
                    <span style={{ fontSize: 12, color: C.soft }}>{t('Prihvat = status/datum prihvata/predračun/račun (normalizirano)', 'Acceptance normalized (status/accept date/proforma/invoice)')}</span>
                    <span style={{ marginLeft: 'auto' }}><CacheNote info={data._cache} locale={locale} /></span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={S.th}>{t('Mjesec', 'Month')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Ponude', 'Offers')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Ponude €', 'Offers €')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Prihvaćene', 'Accepted')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Prihvaćeno €', 'Accepted €')}</th>
                                <th style={S.th}>{t('Konverzija', 'Conversion')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Predr.', 'Prof.')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Rač.', 'Inv.')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Istekle', 'Expired')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Pros. h do prihvata', 'Avg h to accept')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((r) => (
                                <tr key={r.mjesec}>
                                    <td style={{ ...S.td, fontWeight: 600 }}>{r.mjesec}</td>
                                    <td style={S.tdNum}>{fmtN(r.ponude)}</td>
                                    <td style={S.tdNum}>{fmtEur(r.ponude_eur, true)}</td>
                                    <td style={{ ...S.tdNum, color: pm.color, fontWeight: 600 }}>{fmtN(r.prihvacene)}</td>
                                    <td style={S.tdNum}>{fmtEur(r.prihvaceno_eur, true)}</td>
                                    <td style={S.td}><PctBar pct={r.konverzija_pct} color={pm.color} /></td>
                                    <td style={S.tdNum}>{fmtN(r.predracuni)}</td>
                                    <td style={S.tdNum}>{fmtN(r.racuni)}</td>
                                    <td style={S.tdNum}>{fmtN(r.istekle)}</td>
                                    <td style={S.tdNum}>{r.avg_sati_do_prihvata !== null ? nfG.format(r.avg_sati_do_prihvata) + ' h' : '-'}</td>
                                </tr>
                            ))}
                            {data.rows.length === 0 ? (
                                <tr><td style={S.td} colSpan={10}>{t('Nema ponuda u razdoblju.', 'No offers in period.')}</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
                {data.napomena ? <div style={{ fontSize: 11, color: C.soft, marginTop: 8 }}>{data.napomena}</div> : null}
            </div>
        </div>
    );
}

// ------------------------------------------------------------------ finoca

/** Otkup loma po finoci (iz sifre artikla IRA stavki: AU585/AU750/AU999...). */
function FinocaTab({ token, locale, qs }: { token: string | null; locale: Locale; qs: string }) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const [data, setData] = useState<FinocaResp | null>(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        setLoading(true); setErr(null);
        api<FinocaResp>(`/ado-1111/lom-finoca?${qs}`, token)
            .then((d) => { if (alive) setData(d); })
            .catch((e: Error) => { if (alive) setErr(e.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token, qs]);

    if (err) return <div style={{ ...S.card, color: C.bad }}>{err}</div>;
    if (loading || !data) return <Spinner label={t('Učitavam finoće…', 'Loading fineness…')} />;

    const lm = TYPE_META.otkup_lom;
    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{t('Otkup loma po finoći', 'Scrap buyback by fineness')}</span>
                    <Badge color={lm.color} bg={lm.bg}>{t('Ukupno', 'Total')}: {fmtEur(data.total_eur, true)}</Badge>
                    {!data.ira_available ? <Badge color={C.bad} bg="#fef2f2">{t('IRA stavke nedostupne u ovoj bazi', 'IRA entries unavailable in this DB')}</Badge> : null}
                    <span style={{ marginLeft: 'auto' }}><CacheNote info={data._cache} locale={locale} /></span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={S.th}>Metal</th>
                                <th style={S.th}>{t('Finoća', 'Fineness')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>{t('Broj', 'Count')}</th>
                                <th style={{ ...S.th, textAlign: 'right' }}>EUR</th>
                                <th style={S.th}>{t('Udio', 'Share')}</th>
                                <th style={S.th}>{t('Izvor', 'Source')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((r, i) => (
                                <tr key={i}>
                                    <td style={S.td}>{r.metal}</td>
                                    <td style={{ ...S.td, fontWeight: 600 }}>
                                        {r.finoca !== null ? r.finoca : t('nepoznato (live feed)', 'unknown (live feed)')}
                                        {r.finoca === null && (r.au_fine_g || r.ag_fine_g)
                                            ? <span style={{ color: C.muted, fontWeight: 400 }}> · Au {fmtG(r.au_fine_g ?? 0)} / Ag {fmtG(r.ag_fine_g ?? 0)}</span>
                                            : null}
                                    </td>
                                    <td style={S.tdNum}>{fmtN(r.broj)}</td>
                                    <td style={{ ...S.tdNum, fontWeight: 600 }}>{fmtEur(r.eur, true)}</td>
                                    <td style={S.td}><PctBar pct={r.udio_pct} color={lm.color} /></td>
                                    <td style={S.td}><span style={{ fontSize: 11, color: C.soft }}>{sourceLabel(r.izvor, locale)}</span></td>
                                </tr>
                            ))}
                            {data.rows.length === 0 ? (
                                <tr><td style={S.td} colSpan={6}>{t('Nema podataka u razdoblju.', 'No data in period.')}</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
                {data.napomena ? <div style={{ fontSize: 11, color: C.soft, marginTop: 8 }}>{data.napomena}</div> : null}
            </div>
        </div>
    );
}

// ------------------------------------------------------------------ kanali

/** Mjesecna tablica za kanal (veleprodaja / mjenjacnica / zalog agregati). */
function KanalTable({ head, rows, empty }: { head: string[]; rows: (string | number | null)[][]; empty: string }) {
    return (
        <div style={{ overflowX: 'auto', maxHeight: 340, overflowY: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                    <tr>{head.map((h, i) => <th key={h} style={{ ...S.th, ...(i > 0 ? { textAlign: 'right' } : {}), position: 'sticky', top: 0, background: C.surface }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>
                            {r.map((c, j) => (
                                <td key={j} style={j > 0 ? S.tdNum : { ...S.td, fontWeight: 600 }}>{c ?? '-'}</td>
                            ))}
                        </tr>
                    ))}
                    {rows.length === 0 ? <tr><td style={S.td} colSpan={head.length}>{empty}</td></tr> : null}
                </tbody>
            </table>
        </div>
    );
}

/**
 * Ostali kanali: veleprodaja (SLO/BG, Faros tjedni izvoz) te mjenjacnica i
 * zalog (samo agregati: Faros tjedni + mjesecni tabak RUC/Plan). Jasno
 * oznaceno da to NIJE transakcijski feed.
 */
function KanaliTab({ token, locale, qs }: { token: string | null; locale: Locale; qs: string }) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const [vele, setVele] = useState<VeleResp | null>(null);
    const [ostali, setOstali] = useState<KanaliResp | null>(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        setLoading(true); setErr(null);
        Promise.all([
            api<VeleResp>(`/ado-1111/veleprodaja?${qs}`, token),
            api<KanaliResp>(`/ado-1111/ostali-kanali?${qs}`, token),
        ])
            .then(([v, o]) => { if (alive) { setVele(v); setOstali(o); } })
            .catch((e: Error) => { if (alive) setErr(e.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token, qs]);

    if (err) return <div style={{ ...S.card, color: C.bad }}>{err}</div>;
    if (loading || !vele || !ostali) return <Spinner label={t('Učitavam kanale…', 'Loading channels…')} />;

    // tabak pivot: mjesec x poslovnica -> {RUC, Plan}
    const pivotTabak = (rows: KanalTabakRow[]): (string | number | null)[][] => {
        const map = new Map<string, { mjesec: string; poslovnica: string; ruc: number | null; plan: number | null }>();
        for (const r of rows) {
            const k = r.mjesec + '|' + r.poslovnica;
            const e = map.get(k) ?? { mjesec: r.mjesec, poslovnica: r.poslovnica, ruc: null, plan: null };
            if (r.metric === 'Plan') e.plan = (e.plan ?? 0) + r.eur; else e.ruc = (e.ruc ?? 0) + r.eur;
            map.set(k, e);
        }
        return Array.from(map.values()).map((e) => [e.mjesec + ' · ' + e.poslovnica, fmtEur(e.ruc, true), fmtEur(e.plan, true)]);
    };

    const kanalBlok = (title: string, b: KanalBlock) => (
        <div style={S.card}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{title}</span>
                <Badge>{t('Agregat - nije feed', 'Aggregate - not a feed')}</Badge>
            </div>
            <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
                <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{t('Faros tjedni (mjesec × poslovnica)', 'Faros weekly (month × branch)')}</div>
                    <KanalTable
                        head={[t('Mjesec · poslovnica', 'Month · branch'), t('Broj', 'Count'), 'EUR']}
                        rows={b.weekly.map((r) => [r.mjesec + ' · ' + r.poslovnica, fmtN(r.broj), fmtEur(r.eur, true)])}
                        empty={t('Nema tjednih podataka u razdoblju.', 'No weekly data in period.')}
                    />
                </div>
                <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{t('Tabak mjesečno (RUC / Plan)', 'Monthly sheet (margin / plan)')}</div>
                    <KanalTable
                        head={[t('Mjesec · poslovnica', 'Month · branch'), 'RUC', 'Plan']}
                        rows={pivotTabak(b.tabak)}
                        empty={t('Nema tabak podataka u razdoblju.', 'No sheet data in period.')}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            {/* veleprodaja */}
            <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{t('Veleprodaja zlata (SLO / BG)', 'Wholesale gold (SLO / BG)')}</span>
                    <span style={{ fontSize: 12, color: C.soft }}>{t('Izvor: Faros tjedni izvoz', 'Source: Faros weekly export')}</span>
                    <span style={{ marginLeft: 'auto' }}><CacheNote info={vele._cache} locale={locale} /></span>
                </div>
                {!vele.available ? (
                    <div style={{ fontSize: 13, color: C.muted }}>{t('Tjedni izvoz još nije dostupan u ovoj bazi.', 'Weekly export not yet available in this database.')}</div>
                ) : (
                    <>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                            <MiniKpi label={t('Isporuke', 'Deliveries')} value={fmtN(vele.totals?.broj ?? 0)} />
                            <MiniKpi label="EUR" value={fmtEur(vele.totals?.eur ?? 0, true)} />
                            <MiniKpi label={t('Fino zlato', 'Fine gold')} value={fmtG(vele.totals?.fine_g ?? 0)} />
                        </div>
                        <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{t('Mjesečno po firmi/partneru', 'Monthly by company/partner')}</div>
                                <KanalTable
                                    head={[t('Mjesec · partner', 'Month · partner'), t('Broj', 'Count'), 'EUR', t('Fino g', 'Fine g')]}
                                    rows={vele.monthly.map((r) => [r.mjesec + ' · ' + (r.company !== '-' ? r.company : r.branch), fmtN(r.broj), fmtEur(r.eur, true), fmtG(r.fine_g)])}
                                    empty={t('Nema podataka u razdoblju.', 'No data in period.')}
                                />
                            </div>
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{t('Zadnje stavke', 'Latest items')}</div>
                                <KanalTable
                                    head={[t('Vrijeme · ref', 'Time · ref'), t('Partner', 'Partner'), 'EUR', t('Fino g', 'Fine g')]}
                                    rows={vele.rows.slice(0, 100).map((r) => [fmtDT(r.ts) + ' · ' + r.ref, r.company !== '-' ? r.company : r.branch, fmtEur(r.eur, true), r.fine_g !== null ? fmtG(r.fine_g) : '-'])}
                                    empty={t('Nema stavki u razdoblju.', 'No items in period.')}
                                />
                            </div>
                        </div>
                        {vele.napomena ? <div style={{ fontSize: 11, color: C.soft, marginTop: 8 }}>{vele.napomena}</div> : null}
                    </>
                )}
            </div>

            {kanalBlok(t('Mjenjačnica', 'Exchange office'), ostali.mjenjacnica)}
            {kanalBlok(t('Zalog', 'Pawn'), ostali.zalog)}
            {ostali.napomena ? <div style={{ fontSize: 11, color: C.soft }}>{ostali.napomena}</div> : null}
        </div>
    );
}

// --------------------------------------------------------------- glavni modul

type TabKey = 'feed' | 'pregled' | 'analytics' | 'funnel' | 'finoca' | 'kanali' | 'map' | 'branches' | 'plan';

export function Ado1111Module({ token, locale, onExit }: { token: string | null; locale: Locale; onExit: () => void }) {
    const t = (hr: string, en: string) => (locale === 'hr' ? hr : en);
    const [tab, setTab] = useState<TabKey>('feed');
    const [filters, setFilters] = useState<Filters>(defaultFilters());
    const [meta, setMeta] = useState<MetaResp | null>(null);
    const [showCoverage, setShowCoverage] = useState(false);
    // fresh > 0: gumb Osvjezi je stisnut, backendu ide fresh=1 (preskoci cache).
    // Svaka promjena filtera vraca fresh na 0 kroz applyFilters, pa se dalje cita normalno.
    const [fresh, setFresh] = useState(0);
    const applyFilters = useCallback((f: Filters) => { setFresh(0); setFilters(f); }, []);
    const qs = useMemo(() => buildQs(filters) + (fresh > 0 ? `&fresh=1&rf=${fresh}` : ''), [filters, fresh]);

    useEffect(() => {
        let alive = true;
        api<MetaResp>('/ado-1111/meta', token)
            .then((m) => { if (alive) setMeta(m); })
            .catch(() => { /* meta je opcionalan za render */ });
        return () => { alive = false; };
    }, [token]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onExit(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onExit]);

    // Geo opcije: kaskada grupa -> drzava -> subregija -> poslovnica iz meta.branches.
    const branchesAll = meta?.branches ?? [];
    const groups = useMemo(() => Array.from(new Set(branchesAll.map((b) => b.group))).sort(), [branchesAll]);
    const countries = useMemo(
        () => Array.from(new Set(branchesAll.filter((b) => !filters.group || b.group === filters.group).map((b) => b.country))).sort(),
        [branchesAll, filters.group],
    );
    const subregions = useMemo(
        () => Array.from(new Set(branchesAll
            .filter((b) => (!filters.group || b.group === filters.group) && (!filters.country || b.country === filters.country))
            .map((b) => b.subregion))).sort(),
        [branchesAll, filters.group, filters.country],
    );
    const branchOptions = useMemo(
        () => branchesAll
            .filter((b) => (!filters.group || b.group === filters.group)
                && (!filters.country || b.country === filters.country)
                && (!filters.subregion || b.subregion === filters.subregion))
            .map((b) => b.branch)
            .sort(),
        [branchesAll, filters.group, filters.country, filters.subregion],
    );

    const set = (patch: Partial<Filters>) => applyFilters({ ...filters, ...patch });

    const minDate = useMemo(() => {
        const mins = meta ? Object.values(meta.ranges).map((r) => r.min).filter((v): v is string => !!v) : [];
        return mins.length ? mins.sort()[0].slice(0, 10) : '2020-01-01';
    }, [meta]);

    const presets: { label: string; from: string; to: string }[] = useMemo(() => {
        const y = TODAY.slice(0, 4);
        const m = TODAY.slice(0, 7);
        return [
            { label: t('Danas', 'Today'), from: TODAY, to: TODAY },
            { label: '7d', from: isoDaysAgo(7), to: TODAY },
            { label: '30d', from: isoDaysAgo(30), to: TODAY },
            { label: 'MTD', from: `${m}-01`, to: TODAY },
            { label: 'YTD', from: `${y}-01-01`, to: TODAY },
            { label: t('Sve', 'All'), from: minDate, to: TODAY },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minDate, locale]);

    const tabs: { key: TabKey; hr: string; en: string }[] = [
        { key: 'feed', hr: 'Feed', en: 'Feed' },
        { key: 'pregled', hr: 'Pregled', en: 'Overview' },
        { key: 'analytics', hr: 'Analitika', en: 'Analytics' },
        { key: 'funnel', hr: 'Lijevak', en: 'Funnel' },
        { key: 'finoca', hr: 'Finoća', en: 'Fineness' },
        { key: 'kanali', hr: 'Kanali', en: 'Channels' },
        { key: 'map', hr: 'Karta', en: 'Map' },
        { key: 'branches', hr: 'Poslovnice', en: 'Branches' },
        { key: 'plan', hr: 'Plan', en: 'Plan' },
    ];

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 4000, background: C.bg, overflowY: 'auto',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            color: C.text,
        }}>
            <style>{'@keyframes ado1111spin { to { transform: rotate(360deg); } }'}</style>

            {/* zaglavlje */}
            <div style={{
                position: 'sticky', top: 0, zIndex: 10, background: 'rgba(255,255,255,0.94)', backdropFilter: 'blur(6px)',
                borderBottom: `1px solid ${C.border}`, padding: '10px 20px',
                display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
            }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span style={{ fontSize: 16, fontWeight: 700 }}>1111 · {t('Zlato — feed i analitika', 'Gold — feed & analytics')}</span>
                    <span style={{ fontSize: 12, color: C.soft }}>{ALL_TYPES.map((ty) => (locale === 'hr' ? TYPE_META[ty].shortHr : TYPE_META[ty].shortEn)).join(' · ')}</span>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                    {tabs.map((tb) => (
                        <button
                            key={tb.key}
                            onClick={() => setTab(tb.key)}
                            style={{
                                ...S.btn,
                                border: 'none', borderRadius: 8, padding: '7px 14px',
                                background: tab === tb.key ? C.text : 'transparent',
                                color: tab === tb.key ? '#fff' : C.muted,
                                fontWeight: 600,
                            }}
                        >
                            {locale === 'hr' ? tb.hr : tb.en}
                        </button>
                    ))}
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    {tab !== 'plan' ? (
                        <button
                            style={S.btn}
                            title={t('Preskoči cache i preračunaj iz baze', 'Skip cache and recompute from the database')}
                            onClick={() => setFresh((v) => v + 1)}
                        >
                            ↻ {t('Osvježi', 'Refresh')}
                        </button>
                    ) : null}
                    <button style={S.btn} onClick={onExit}>✕ {t('Izlaz', 'Exit')} (Esc)</button>
                </div>
            </div>

            <div style={{ maxWidth: 1440, margin: '0 auto', padding: '16px 20px 60px', display: 'grid', gap: 14 }}>
                {/* globalni filteri (Plan tab ih ne koristi) */}
                {tab !== 'plan' ? (
                    <div style={{ ...S.card, display: 'grid', gap: 10 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <div>
                                <span style={S.label}>{t('Razdoblje', 'Period')}</span>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                    <input type="date" style={S.input} value={filters.dateFrom} min={minDate} max={filters.dateTo} onChange={(e) => set({ dateFrom: e.target.value })} />
                                    <span style={{ color: C.soft }}>→</span>
                                    <input type="date" style={S.input} value={filters.dateTo} min={filters.dateFrom} max={TODAY} onChange={(e) => set({ dateTo: e.target.value })} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                                {presets.map((p) => {
                                    const active = filters.dateFrom === p.from && filters.dateTo === p.to;
                                    return (
                                        <button key={p.label} style={{ ...S.btn, padding: '6px 10px', fontSize: 12, background: active ? C.text : C.surface, color: active ? '#fff' : C.muted }} onClick={() => set({ dateFrom: p.from, dateTo: p.to })}>
                                            {p.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <div>
                                <span style={S.label}>{t('Grupa država', 'Country group')}</span>
                                <select style={S.input} value={filters.group} onChange={(e) => set({ group: e.target.value, country: '', subregion: '', branches: [] })}>
                                    <option value="">{t('Sve', 'All')}</option>
                                    {groups.map((g) => <option key={g} value={g}>{g}</option>)}
                                </select>
                            </div>
                            <div>
                                <span style={S.label}>{t('Država', 'Country')}</span>
                                <select style={S.input} value={filters.country} onChange={(e) => set({ country: e.target.value, subregion: '', branches: [] })}>
                                    <option value="">{t('Sve', 'All')}</option>
                                    {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <span style={S.label}>{t('Pod-regija', 'Subregion')}</span>
                                <select style={S.input} value={filters.subregion} onChange={(e) => set({ subregion: e.target.value, branches: [] })}>
                                    <option value="">{t('Sve', 'All')}</option>
                                    {subregions.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div>
                                <span style={S.label}>{t('Poslovnica', 'Branch')}</span>
                                <select style={S.input} value={filters.branches[0] ?? ''} onChange={(e) => set({ branches: e.target.value ? [e.target.value] : [] })}>
                                    <option value="">{t('Sve', 'All')}</option>
                                    {branchOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                                </select>
                            </div>
                            <div>
                                <span style={S.label}>Metal</span>
                                <select style={S.input} value={filters.metal} onChange={(e) => set({ metal: e.target.value })}>
                                    <option value="">{t('Svi', 'All')}</option>
                                    {(meta?.metals ?? ['Zlato', 'Srebro']).map((m) => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>
                            <div>
                                <span style={S.label}>{t('Status (prodaja)', 'Status (sales)')}</span>
                                <select style={S.input} value={filters.status} onChange={(e) => set({ status: e.target.value })}>
                                    <option value="">{t('Svi', 'All')}</option>
                                    {(meta?.statuses ?? []).map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                                <span style={S.label}>{t('Traži', 'Search')}</span>
                                <input style={{ ...S.input, width: '100%', boxSizing: 'border-box' }} placeholder={t('Ref, artikl, partner…', 'Ref, article, partner…')} value={filters.q} onChange={(e) => set({ q: e.target.value })} />
                            </div>
                            <div>
                                <span style={S.label}>Min €</span>
                                <input style={{ ...S.input, width: 90 }} inputMode="decimal" placeholder="0" value={filters.minEur} onChange={(e) => set({ minEur: e.target.value })} />
                            </div>
                            <button style={{ ...S.btn, color: C.muted }} onClick={() => setFilters(defaultFilters())}>{t('Reset', 'Reset')}</button>
                        </div>
                        {meta ? (
                            <div style={{ fontSize: 12, color: C.soft }}>
                                <button
                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: C.muted, fontSize: 12, fontFamily: 'inherit', textDecoration: 'underline' }}
                                    onClick={() => setShowCoverage((v) => !v)}
                                >
                                    {t('Pokrivenost podataka', 'Data coverage')} {showCoverage ? '▴' : '▾'}
                                </button>
                                {showCoverage ? (
                                    <div style={{ marginTop: 6, display: 'grid', gap: 3 }}>
                                        {ALL_TYPES.map((ty) => (
                                            <div key={ty} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                <TypeDot type={ty} />
                                                <span style={{ color: C.muted, minWidth: 110 }}>{locale === 'hr' ? TYPE_META[ty].shortHr : TYPE_META[ty].shortEn}</span>
                                                <span>{fmtD(meta.ranges[ty]?.min)} — {fmtD(meta.ranges[ty]?.max)}</span>
                                                {ty === 'otkup_inv' ? <span style={{ color: C.soft }}>{t('(samo live razdoblje)', '(live period only)')}</span> : null}
                                            </div>
                                        ))}
                                        {meta.coverage_prodaja.length ? (
                                            <div style={{ marginTop: 4 }}>
                                                {t('Prodaja po izvorima:', 'Sales by source:')}{' '}
                                                {meta.coverage_prodaja.map((c) => `${sourceLabel(c.source, locale)} ${fmtN(c.broj)} (${fmtD(c.od)}—${fmtD(c.do)})`).join(' · ')}
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {/* tabovi */}
                {tab === 'feed' ? <FeedTab token={token} locale={locale} filters={filters} setFilters={applyFilters} qs={qs} /> : null}
                {tab === 'pregled' ? <PregledTab token={token} locale={locale} qs={qs} /> : null}
                {tab === 'analytics' ? <AnalyticsTab token={token} locale={locale} filters={filters} qs={qs} /> : null}
                {tab === 'funnel' ? <FunnelTab token={token} locale={locale} qs={qs} /> : null}
                {tab === 'finoca' ? <FinocaTab token={token} locale={locale} qs={qs} /> : null}
                {tab === 'kanali' ? <KanaliTab token={token} locale={locale} qs={qs} /> : null}
                {tab === 'map' ? <MapTab token={token} locale={locale} filters={filters} setFilters={applyFilters} qs={qs} /> : null}
                {tab === 'branches' ? <BranchesTab token={token} locale={locale} filters={filters} qs={qs} /> : null}
                {tab === 'plan' ? <PlanTab token={token} locale={locale} meta={meta} /> : null}
            </div>
        </div>
    );
}





